/**
 * @file TransportPlanning.js
 * @version 3.13.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-01-19
 * @modified 2026-11-03
 * @description Transport planning board: per-line tonnage + vehicle load checker
 *              (payload + compartment fit), driver/haulier/vehicle assignment,
 *              drag-drop sequencing, day summary rollup, bulk row selection
 *              (group / move / bulk-set), multi-row block drag with auto-scroll.
 *              Stock-supplied lines (H J Ward Agriculture / Excel Agri Ltd) never
 *              raise a PO, so they arrive with is_stock_line and are keyed by
 *              order line (SL-) rather than PO id — see utils/planningRows.js.
 *              The server sends them to the Haulage/Bag board, but one moved
 *              here by hand must still render: it has no PO status.
 *              Each row has a three-dots menu beside the right-click one;
 *              both offer "Archive order…" / "Archive PO…", one row at a time,
 *              through the search screens' own flows (hooks/useBoardArchive).
 *
 *              2.44.0 (2026-10-07): Loads and Drops, when the transport_day_plan
 *              switch is on (utils/dayPlan.js). Each bulk row shows L# · C# · D#
 *              beside its time; a lorry's rows are listed in day order (by load,
 *              then drop); dragging a row within its load moves its drop (saved
 *              as that lorry's override, PUT /planning/day-plan); one line per
 *              lorry shows the day ("L1 Risdon 06:00 › D1 …") coloured by its
 *              checks; the load cards and window are driven from the day.
 *              "Combine into one run" is greyed — two collections are simply two
 *              loads. Switched off, everything is exactly as before.
 *
 *              2.45.0 (2026-10-07): the Day window (DayPlanWindow) opens from a
 *              lorry's day line and its load cards; one load card per same-time
 *              round; the engine is guarded (the server's days if it throws);
 *              the board's day order is saved once, after a pause, by someone
 *              who may plan on an open day, so the paperwork prints it.
 *
 *              2.46.0 (2026-10-07, review): viewing never saves the order (the
 *              automatic save is gone — paperwork prints the server's day);
 *              overrides are pruned before they are sent; an engine failure
 *              leaves the board read-only on the server's days; Space opens a
 *              day line.
 *
 *              2.46.1 (2026-10-07): the load window's layouts are greyed with
 *              the reason on a locked or past day (the server refuses a
 *              reorder and a door layout there), rather than applied and undone.
 *
 *              3.0.0 (2026-10-12): Loads and Drops scrapped for LORRY DAY
 *              PLANNING (same switch, transport_day_plan; utils/lorryDay.js).
 *              Switched on, the table is grouped by haulier, driver and
 *              vehicle (unassigned first; Customer Collection, Direct Delivery
 *              and third-party haulage in their own groups), rows in time
 *              order; each row has a collection time and a DROP time, its
 *              compartments, a planned tick (✓ ◐ ○) and the planners' own NOTE
 *              (never printed or sent); each lorry's header carries its
 *              efficiency, peak payload and checks, and "Open day" opens the
 *              Lorry day window (LorryDayWindow — the lorry drawing, doors,
 *              compartments). Dragging rows onto another group assigns them to
 *              it. Checks are guidance; nothing is refused or moved over them.
 *              The arrow keys move between time fields, on or off.
 *              Switched off, the board is as it was.
 *
 *              3.1.0 (2026-10-12, Cam): SELF-BILLED purchase orders are rows
 *              (utils/selfBilledPo — keyed sb:<PO>:<line>, badged
 *              "Self-billed", the delivery / storage address in the customer
 *              column). They take haulier, driver, vehicle and times like any
 *              row and never need planning: the mill emails leave them out,
 *              driver paperwork leaves out the unplanned ones and the send
 *              windows (driver and office) say which. Not moved by date or
 *              board here; with lorry day planning on they are collect and
 *              drop stops with no drop time, compartments or note.
 *              3.1.1 (2026-10-13, review): a self-billed PO the seller
 *              delivers or a third party carries is no delivery of ours — out
 *              of driver paperwork, the loads, runs and the day's counts
 *              (utils/selfBilledPo carriedByOthers), as Customer Collection.
 *              3.2.0 (2026-10-13, Cam): a self-billed row we carry takes a drop
 *              time, compartments and a planner's note like any order row
 *              (kept by PO line); greyed only while the server stamps it
 *              no_line_plan (before that database update).
 *              3.3.0 (2026-10-14, Cam): an order on one of its option dates
 *              that is not its delivery date (utils/planningDay
 *              isOptionCandidate) counts only as a candidate — no load,
 *              compartment, drop number or run, not in the day / office
 *              summary, paperwork or mill emails. Del. Inst. is the order's
 *              instructions with the PO's own text (worked out by the server);
 *              the office summary no longer prints them twice.
 *
 *              3.4.0 (2026-10-14, Cam): driver paperwork straight to the
 *              drivers — the send window says when a pack would go to the
 *              haulier's address because the driver has none (forwarded
 *              from admin@ it bounced at iCloud), lets the driver's own
 *              address be added there, and can Cc the office
 *              (utils/driverPaperworkRecipients, shared by both boards).
 *
 *              3.5.0 (2026-10-15, Cam): a refused order's redelivery
 *              (utils/redelivery) is a row on its redelivery date, badged
 *              "Redelivery" and keyed by its order line for time / order /
 *              doors / runs (utils/planningRows boardKeyOf); "Refused —
 *              redeliver…" / "Edit redelivery…" in each row's menus
 *              (hooks/useBoardRedeliver). Not moved to another date or board
 *              from here, its quantity not changed (the whole line goes).
 *
 *              3.6.0 (2026-10-15, Cam — lorry day planning live):
 *                - each lorry's group collapses (its chevron; remembered per
 *                  date in this tab), its header and summary kept;
 *                - a typed collection time fills the drop time when that is
 *                  blank or earlier (lorryDay.dropTimeAfterCollect);
 *                - rows are NOT re-ordered as times change: a row now out of
 *                  drop order shows "!", and "Re-order by time" on the
 *                  lorry's header sorts its rows first drop first and saves
 *                  the order (utils/lorryDayOrder);
 *                - over-filled compartments and payloads are flagged on the
 *                  lorry's header and in the driver paperwork send window,
 *                  never refused;
 *                - every time field is PlanTimeInput (faded, typed over,
 *                  10.5 → 10:30, 1012 → 10:12);
 *                - one planner at a time (hooks/usePlanningControl,
 *                  PlanningControlBar): another planner's board is view only,
 *                  with the hand-over prompts.
 *
 *              3.13.0 (2026-11-03, Cam — "Please check the order of rows
 *              in the send to office email … it seems quite confusing as it
 *              is"): the office summary is sent grouped as the board groups
 *              it (utils/boardGroups — groupOf and the group order moved
 *              there, so the board and the summary cannot drift): not yet
 *              assigned first, then our lorries by first collection then
 *              registration, then outside hauliers'; each lorry's rows in
 *              drop order (lorry day rules) or by collection time (old rules,
 *              a run at its first time); each row with its collection and
 *              drop times and its D#. `groups` and the flat `deliveries`, in
 *              the same order. The board itself is unchanged.
 *
 *              3.12.0 (2026-10-30, Cam — "Can those slideouts and the
 *              warning icon all be expanded rather than slideout please"):
 *              the header's Mills / Paperwork / Office emailed notices are
 *              always open, and a red "email not delivered" notice joins them
 *              when any of the day's transport emails bounced or failed — one
 *              component for both boards (TransportEmailNotices), which opens
 *              Plan History on its Emails tab (initialTab).
 *
 *              3.11.0 (2026-10-26, Cam — "If user chooses a haulier other
 *              than Excel agri in transport planning, and there is no PO-H
 *              raised for that order, can it prompt them to raise and send
 *              one"): after an outside haulier is saved on a row (the haulier
 *              cell, a driver bringing their haulier, the bulk edit, rows
 *              dragged onto another haulier's group), the board asks once per
 *              change whether to raise the order's PO-H and send it
 *              (hooks/useOutsideHaulierPoh, OutsideHaulierPohDialog). Never on
 *              a past or locked day, view only, or another planner's day.
 *
 *              3.9.0 (2026-10-23, Cam): one planner per board DAY, not per
 *              board (hooks/usePlanningControl 1.1.0) — the board claims the
 *              day it shows and lets it go on moving to another. The Lorry
 *              day window saves as it changes (LorryDayWindow 1.4.0): the
 *              board's rows follow each save at once, and the board is read
 *              again when the window closes; a day change closes the window
 *              (what was left is saved on that day).
 *
 *              3.7.0 (2026-10-20, Cam): the driver paperwork send window has a
 *              "Detailed day sheet" switch per driver, off on every send: on,
 *              the lorry day sheet; off, one row per order (ForFarmers with
 *              their compartments). Greyed with the reason on a day the
 *              server says has no detailed sheet (GET
 *              /planning/paperwork-layout/:date); sent as `detailed` on each
 *              driverSelections entry.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { normalizeTimeValue } from '../utils/planningTime';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import PaperworkSendModal from './PaperworkSendModal';
import {
  buildDriverPaperworkRecipients, fallbackNote, fallbackWarning, withDriverEmail, saveDriverEmail
} from '../utils/driverPaperworkRecipients';
import PaperworkHistoryModal from './PaperworkHistoryModal';
import PlanHistoryModal from './PlanHistoryModal';
import TransportEmailNotices, { HeaderNotice } from './TransportEmailNotices';
import ChangeQuantityModal from './ChangeQuantityModal';
import { revisedPosFrom } from '../utils/poRevisions';
import { analyzeVehicleLoad, segmentLoads, fitSeverity, efficiencySeverity, SEVERITY_COLOR, compartmentsByRow, dropNumbersByRow, loadTimeLabel } from '../utils/vehicleLoad';
import ProgressRing from './ProgressRing';
import VehicleLoadModal from './VehicleLoadModal';
import LorryDayWindow from './LorryDayWindow';
import PlanTimeInput from './PlanTimeInput';
import PlannerNoteModal from './PlannerNoteModal';
import PlanningControlBar from './PlanningControlBar';
import usePlanningControl from '../hooks/usePlanningControl';
import { outOfOrderIds, reorderGroup } from '../utils/lorryDayOrder';
import { groupOf, groupRows, officeSummaryLayout } from '../utils/boardGroups';
import CustomerOrderModal from './CustomerOrderModal';
import './SearchLayout.css';
import './TransportPlanning.css';
import './AuditHistoryModal.css';
import LoadingSpinner from './LoadingSpinner';
import SearchableDropdown from './SearchableDropdown';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import SendToSupplierFlow from './SendToSupplierFlow';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import OfficeSummaryModal from './OfficeSummaryModal';
import { useDragSelect } from '../hooks/useDragSelect';
import { hasPermission, badgeOf } from '../utils/permissions';
import usePersistedState from '../hooks/usePersistedState';
import useTableContextMenu from '../hooks/useTableContextMenu';
import TableContextMenu from './TableContextMenu';
import RowActionsMenu from './RowActionsMenu';
import useBoardArchive from '../hooks/useBoardArchive';
import useBoardRedeliver from '../hooks/useBoardRedeliver';
import useOutsideHaulierPoh from '../hooks/useOutsideHaulierPoh';
import {
  REDELIVERY_LABEL, REDELIVERY_DATE_REASON, REDELIVERY_BOARD_REASON, REDELIVERY_QTY_REASON,
  redeliveryTitle, redeliveryRowPlanned
} from '../utils/redelivery';
import PlanningBulkEditModal from './PlanningBulkEditModal';
import { isStockKey, stockLineIdOf, boardKeyOf, rowReference, transportChangesUrl, RowReferenceLink, UNKNOWN_VEHICLE, isExcelHaulier, newRunKey, saveRun, withRunKey, boardSequence, saveDropTimes, saveLorryDay } from '../utils/planningRows';
import { isOptionCandidate } from '../utils/planningDay';
import { buildLorryDays, rowPlanMap, unknownVehicleSpec, inLorryDay, lorryKeyOf, rowPlanStatus, bodyOf, normaliseLinePlan, otherCarrierOf, dropTimeAfterCollect, lorryOverfill, overfillWarning, isOverfillCheck } from '../utils/lorryDay';
import EntityLink from './EntityLink';
import { formatQuantity, formatTonnes } from '../utils/numberFormatters';
import {
  plannableRowIds, driverPaperworkRows, millEmailRows, ourDeliveryRows, selfBilledRowPlanned, unplannedSelfBilledWarning,
  SELF_BILLED_LABEL, SELF_BILLED_BADGE_TITLE, SELF_BILLED_BOARD_REASON, SELF_BILLED_DATE_REASON, SELF_BILLED_NO_ORDER_REASON
} from '../utils/selfBilledPo';
import { thirdPartyBadgeTitle } from '../utils/thirdPartyBoard';

// With lorry day planning on, each collection has its own time on the lorry's day.
const RUNS_RETIRED = 'Lorry day planning is on: each collection has its own time on the lorry’s day, so runs are not used';
// With lorry day planning on, a lorry's rows are put in time order by its
// header's "Re-order by time" (first drop at the top), not by hand.
const ORDER_BY_TIME = 'Lorry day planning is on: use “Re-order by time” on the lorry’s header to put its rows in time order, or drag a row onto another lorry';

// First three alphabetic characters of a string, uppercased.
const letters3 = (s) => (s || '').replace(/[^a-zA-Z]/g, '').slice(0, 3).toUpperCase();

// Sentinel "vehicle" for a driver whose real vehicle isn't in the fleet (external
// hauliers). Its payload is set per driver per day (transport.unknown_vehicle_payloads)
// rather than living on a vehicles-table row.

// Compact supplier/mill label for the board column. Known suppliers get a fixed
// abbreviation; "MVFS - X" bodies use the first three letters of X; everything
// else uses the first three letters of the name. Full name + account number are
// shown in the cell's tooltip.
const abbreviateSupplier = (name) => {
  if (!name) return '';
  const n = String(name).trim();
  const map = {
    'nwf agriculture': 'NWF',
    'forfarmers - exeter': 'FF',
    'mole valley feed solutions': 'MVFS',
    'mvfs - lifton': 'LIF',
    'mvfs - risdon': 'RIS',
    // Us (own stock / group companies) → EA.
    'excel agri': 'EA',
    'stock': 'EA',
    'h j ward agriculture': 'EA'
  };
  const fixed = map[n.toLowerCase()];
  if (fixed) return fixed;
  const mvfs = n.match(/^mvfs\s*-\s*(.+)$/i);
  if (mvfs) return letters3(mvfs[1]);
  return letters3(n);
};

function TransportPlanning({ user, onNavigate }) {
  // Holds the permission; whether this tab may plan now is canEdit, below.
  const canPlan = hasPermission(user, 'transport_planning');
  // The page is keyed by selectedDate so persisting it is the whole game —
  // returning users land back on the date they were planning.
  const [selectedDate, setSelectedDate] = usePersistedState(
    'transportPlanning.selectedDate',
    (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    })()
  );
  // One planner at a time on this board (hooks/usePlanningControl): another
  // tab holding it makes this one view only. Null before the migration.
  const control = usePlanningControl('bulk', { canPlan, date: selectedDate });
  const controlBlock = control.blockReason;
  const canEdit = canPlan && !controlBlock;
  // Why this tab cannot change the plan (permission, or someone else planning).
  const noEditReason = !canPlan ? 'You do not have permission to edit transport planning' : controlBlock;
  // Lorry groups folded away on the board, per date (this tab).
  const [collapsedGroups, setCollapsedGroups] = usePersistedState('transportPlanning.collapsedGroups', {});
  const [orders, setOrders] = useState([]);
  // Every row on the board — named apart for the summaries, which narrow it
  // to our own deliveries (no Customer Collection) under the name `orders`.
  const boardOrders = orders;
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [hauliers, setHauliers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleSpecs, setVehicleSpecs] = useState({}); // registration -> full spec, for the load checker
  const [unknownPayloads, setUnknownPayloads] = useState({}); // driverName -> payload, for the "Unknown" vehicle
  const [changesModal, setChangesModal] = useState({ open: false, order: null, changes: [], loading: false });
  const [expandedChanges, setExpandedChanges] = useState(new Set());
  const [isLocked, setIsLocked] = useState(false);
  const [isPastDate, setIsPastDate] = useState(false); // True if date is before today (auto-locked)
  const [isSnapshot, setIsSnapshot] = useState(false);
  // Lorry day planning (transport_day_plan): { enabled, ready, fromSnapshot,
  // lorryDays } from the planning payload; { enabled: false } when off.
  const [lorryDayState, setLorryDayState] = useState({ enabled: false });
  // The Lorry day window: the lorry key while open.
  const [lorryWindow, setLorryWindow] = useState(null);
  // A save from it since it opened (the board is read again when it closes),
  // and the day shown now (a save sent for another day leaves the rows alone).
  const lorryDaySavedRef = useRef(false);
  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;
  // Another day: the window is that lorry's day on the old date — it closes
  // (saving what is left as it goes).
  useEffect(() => { setLorryWindow(null); }, [selectedDate]);
  // The planner's note being edited: the board row.
  const [noteFor, setNoteFor] = useState(null);
  const [lockLoading, setLockLoading] = useState(false); // Loading state for lock operations
  const [unlockModal, setUnlockModal] = useState({ open: false, password: '', error: null });
  // A past day unlocked for correction: until when (it re-locks itself).
  const [unlockedUntil, setUnlockedUntil] = useState(null);
  // Order, times, moves, groups, paperwork: fixed while locked, and ALWAYS on
  // a past day — only haulier / driver / vehicle can be corrected after it
  // (services/pastDayUnlockService). Those three cells follow isLocked alone.
  // Someone else planning the board fixes everything, the three cells too.
  const planFixed = isLocked || isPastDate || !!controlBlock;
  const cellsFixed = isLocked || !!controlBlock;
  const [paperworkModal, setPaperworkModal] = useState({ open: false, warnings: [], generating: false, progress: 0 });
  const [haulierSelectionModal, setHaulierSelectionModal] = useState({ open: false, hauliers: [] });
  const [supplierSelectionModal, setSupplierSelectionModal] = useState({ open: false, suppliers: [] });
  const [comingSoonModal, setComingSoonModal] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', type: 'info' });
  const [hauliersWithEmail, setHauliersWithEmail] = useState([]);
  const [paperworkLog, setPaperworkLog] = useState(null);
  const [supplierEmailLog, setSupplierEmailLog] = useState(null);
  const [officeSummaryModal, setOfficeSummaryModal] = useState({ open: false, generating: false });
  const [officeSummaryLog, setOfficeSummaryLog] = useState(null);
  const [expandedAddresses, setExpandedAddresses] = useState(new Set());
  const [datesWithChanges, setDatesWithChanges] = useState(new Set()); // dates (today+future) with unacknowledged changes → warning dot
  const dateSelectorRef = useRef(null);
  const ordersRef = useRef(orders);
  const measureRef = useRef(null);
  const [colWidths, setColWidths] = useState({ haulier: 130, driver: 135, vehicle: 140 });

  // ── Row selection + reorder / context-menu / view state ──
  const [selectedRows, setSelectedRows] = useState(new Set()); // order_line_id set
  // Archive order… / Archive PO… from a row's menus — the search screens' own
  // flows, one row at a time (hooks/useBoardArchive). fetchOrders is declared
  // further down; it is only called once an archive has finished.
  const rowArchive = useBoardArchive({
    user,
    selectedCount: selectedRows.size,
    isSnapshot,
    source: 'Transport planning',
    onArchived: () => { setSelectedRows(new Set()); fetchOrders(); },
    showAlert: ({ title, message, type }) => setAlertDialog({ open: true, title, message, type })
  });
  // Refused — redeliver… / Edit redelivery… from a row's menus, one row at a
  // time (hooks/useBoardRedeliver, RedeliverOrderModal).
  const rowRedeliver = useBoardRedeliver({
    user,
    selectedCount: selectedRows.size,
    onDone: () => { setSelectedRows(new Set()); fetchOrders(); },
    showAlert: ({ title, message, type }) => setAlertDialog({ open: true, title, message, type })
  });
  // An outside haulier set on a row whose order has no PO-H: "raise one and
  // send it?" — once per change, never on a fixed day (hooks/useOutsideHaulierPoh).
  const outsidePoh = useOutsideHaulierPoh({
    user,
    date: selectedDate,
    enabled: canEdit && !planFixed && !isSnapshot,
    onChanged: () => fetchOrders(),
    showAlert: ({ title, message, type }) => setAlertDialog({ open: true, title, message, type })
  });
  // Bulk edit of haulier / driver / vehicle / time for the selected rows.
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [moveToModal, setMoveToModal] = useState({ open: false, value: '', targetIds: null });
  const [combineRunModal, setCombineRunModal] = useState({ open: false, value: '', targetIds: null });
  const [moveDateModal, setMoveDateModal] = useState({ open: false, value: '', orderIds: [], count: 0, optionDates: [] });
  const [notesModal, setNotesModal] = useState({ open: false, order: null });
  const [vehicleLoadsCollapsed, setVehicleLoadsCollapsed] = usePersistedState('transportPlanning.vehicleLoadsCollapsed', false);
  const [daySummaryCollapsed, setDaySummaryCollapsed] = usePersistedState('transportPlanning.daySummaryCollapsed', false);
  const [datePickerCollapsed, setDatePickerCollapsed] = usePersistedState('transportPlanning.datePickerCollapsed', false);
  const [driverColorsOn, setDriverColorsOn] = usePersistedState('transportPlanning.driverColorsOn', false);
  const [isolatedDriver, setIsolatedDriver] = useState(null); // null = off; '' = unassigned only; name = that driver + unassigned
  const [showOnlyOptionPlanned, setShowOnlyOptionPlanned] = useState(false); // only rows planned here that also have option dates
  const [optionSectionCollapsed, setOptionSectionCollapsed] = usePersistedState('transportPlanning.optionSectionCollapsed', true);
  const ctxMenu = useTableContextMenu({
    rows: orders,
    selectedIds: selectedRows,
    getRowId: (o) => o.order_line_id
  });
  // "Go to order" — the customer order behind a row. Persist the id (not the
  // object) so the modal reopens on the row after navigating away and back.
  const [orderModalId, setOrderModalId] = usePersistedState('transportPlanning.orderModalId', null);
  const transportScrollRef = useRef(null);          // the vertical scroll container
  const autoScrollRef = useRef({ raf: null, velocity: 0 }); // drag auto-scroll
  const dragBlockRef = useRef(null);                // order_line_ids dragged as a block
  const assignDragRef = useRef(null);               // lorry day planning: ids dragged onto a group
  const [dropTargetGroup, setDropTargetGroup] = useState(null);
  // Lorry day planning: where rows dragged within their own lorry's group
  // would land — { id: order_line_id, after: bool } (Cam, 22 Oct 2026).
  const [dropTargetRow, setDropTargetRow] = useState(null);

  // Re-measure slideout button widths whenever a modal that contains
  // .btn-slideout buttons opens/closes. The .generating flags are listed
  // for the haulier and supplier modals because their footer (with the
  // slideout buttons) is conditionally rendered while generating; toggling
  // remounts the buttons.
  useSlideOutButton([
    unlockModal.open,
    changesModal.open,
    changesModal.loading,
    paperworkModal.open,
    haulierSelectionModal.open,
    haulierSelectionModal.generating,
    supplierSelectionModal.open,
    supplierSelectionModal.generating,
    officeSummaryModal.open,
    officeSummaryModal.generating,
    moveToModal.open,
    moveDateModal.open,
    // The bulk-actions bar (with its slide-out buttons) mounts/unmounts with
    // the selection, so re-measure whenever the selection count changes.
    selectedRows.size,
    comingSoonModal,
    // Counts on the haulier/supplier "Send to N …" buttons change as the
    // user toggles include checkboxes, so the label width changes too.
    haulierSelectionModal.hauliers && haulierSelectionModal.hauliers.filter(h => h.include).length,
    supplierSelectionModal.suppliers && supplierSelectionModal.suppliers.filter(s => s.include).length,
    // The lorry groups' "Open day" buttons come and go with the switch and the rows.
    lorryDayState.enabled,
    orders.length
  ]);

  // Measure widest haulier/vehicle names to set dynamic column min-widths
  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const measure = (items) => {
      let max = 0;
      items.forEach(text => {
        el.textContent = text;
        max = Math.max(max, el.offsetWidth);
      });
      el.textContent = '';
      return max;
    };
    const pad = 32; // cell padding + dropdown arrow space
    const hw = hauliers.length > 0 ? measure(hauliers) + pad : 180;
    const dw = drivers.length > 0 ? measure(drivers.map(d => d.name)) + pad : 150;
    const vw = vehicles.length > 0 ? measure(vehicles) + pad : 140;
    setColWidths({
      haulier: Math.max(hw, 180),
      driver: Math.max(dw, 150),
      vehicle: Math.max(vw, 140)
    });
  }, [hauliers, drivers, vehicles]);

  // Generate dates: 3 months back through 2 months ahead from today
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(today);
    start.setMonth(start.getMonth() - 3);

    const end = new Date(today);
    end.setMonth(end.getMonth() + 2);

    const current = new Date(start);
    while (current <= end) {
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, '0');
      const dd = String(current.getDate()).padStart(2, '0');
      dates.push(`${yyyy}-${mm}-${dd}`);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const dates = generateDates();

  useEffect(() => {
    // Check if selected date is before today (past dates should be auto-locked)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    const isPast = selected < today;
    setIsPastDate(isPast);

    // Fetch lock status from DB, auto-lock if past date
    fetchLockStatus(selectedDate, isPast);
    setSelectedRows(new Set()); // stale row ids on date change
    setIsolatedDriver(null);
    setShowOnlyOptionPlanned(false);
    ctxMenu.reset();
    fetchOrders();
    fetchUnknownPayloads(selectedDate);
    fetchPaperworkLog(selectedDate);
    fetchSupplierEmailLog(selectedDate);
    fetchOfficeSummaryLog(selectedDate);
    fetchDatesWithChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    fetchHauliers();
    fetchDrivers();
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep ordersRef in sync with orders state for use in drag handlers
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  // Scroll date selector to center on selected date on mount
  useEffect(() => {
    if (dateSelectorRef.current) {
      const container = dateSelectorRef.current;
      const selectedIndex = dates.indexOf(selectedDate);
      const buttons = container.querySelectorAll('.date-btn');
      if (selectedIndex >= 0 && buttons[selectedIndex]) {
        const button = buttons[selectedIndex];
        const containerRect = container.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        const scrollLeft = container.scrollLeft + (buttonRect.left + buttonRect.width / 2) - (containerRect.left + containerRect.width / 2);
        container.scrollLeft = scrollLeft;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollDates = (direction) => {
    if (dateSelectorRef.current) {
      const container = dateSelectorRef.current;
      const scrollAmount = 300; // Scroll by approximately 3 date buttons
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const fetchHauliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/hauliers`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setHauliersWithEmail(data.hauliers);
        setHauliers(data.hauliers.map(h => h.name));
      }
    } catch (error) {
      console.error('Failed to load hauliers:', error);
      setHauliers([]);
      setHauliersWithEmail([]);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/drivers`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        // Keep the full driver shape — name, hauliers (for filter), default vehicle (for auto-fill).
        setDrivers(data.drivers);
      }
    } catch (error) {
      console.error('Failed to load drivers:', error);
      setDrivers([]);
    }
  };

  // Load the set of today/future dates that have jobs with unacknowledged
  // changes — drives the warning dots on the date selector.
  const fetchDatesWithChanges = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/dates-with-changes`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setDatesWithChanges(new Set(data.dates));
      }
    } catch (error) {
      console.error('Failed to load dates with changes:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/vehicles`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setVehicles(data.vehicles.map(v => v.registration));
        setVehicleSpecs(Object.fromEntries(data.vehicles.map(v => [v.registration, v])));
      }
    } catch (error) {
      console.error('Failed to load vehicles:', error);
      setVehicles([]);
    }
  };


  const fetchPaperworkLog = async (date) => {
    try {
      const response = await fetch(
        `${API_URL}/api/transport/paperwork-log/${date}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setPaperworkLog(data.latest);
      }
    } catch (error) {
      console.error('Failed to load paperwork log:', error);
      setPaperworkLog(null);
    }
  };

  const fetchSupplierEmailLog = async (date) => {
    try {
      const response = await fetch(
        `${API_URL}/api/transport/supplier-email-log/${date}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setSupplierEmailLog(data.latest);
      }
    } catch (error) {
      console.error('Failed to load supplier email log:', error);
      setSupplierEmailLog(null);
    }
  };

  const fetchOfficeSummaryLog = async (date) => {
    try {
      const response = await fetch(
        `${API_URL}/api/transport/office-summary-log/${date}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setOfficeSummaryLog(data.latest);
      }
    } catch (error) {
      console.error('Failed to load office summary log:', error);
      setOfficeSummaryLog(null);
    }
  };

  const fetchLockStatus = async (date, isPast) => {
    setLockLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/transport/lock/${date}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        // The server decides: a past day is locked unless it was unlocked
        // for correction within the last half hour, and re-locks itself.
        setIsLocked(data.isLocked);
        setUnlockedUntil(data.unlockedUntil || null);
      }
    } catch (error) {
      console.error('Failed to load lock status:', error);
      // Default to locked for past dates, unlocked for future
      setIsLocked(isPast);
    } finally {
      setLockLoading(false);
    }
  };

  const saveLockStatus = async (date, locked) => {
    try {
      const response = await fetch(`${API_URL}/api/transport/lock/${date}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isLocked: locked })
      });
      const data = await response.json();
      if (data.success) {
        setIsLocked(data.isLocked);
        setUnlockedUntil(data.unlockedUntil || null);
      }
      return data.success;
    } catch (error) {
      console.error('Failed to save lock status:', error);
      return false;
    }
  };

  /*
   * One request for every selected row: the server writes them in a single
   * transaction and recalculates each order's status once. The board is then
   * re-read rather than patched, so the vehicle loads and summaries match.
   */
  const applyBulkEdit = async (changes) => {
    // Order lines and self-billed POs' rows (sb:<PO>:<line>) alike.
    const orderLineIds = plannableRowIds(selectedRows);
    const haulageJobIds = [];
    const editedRows = orders.filter(o => selectedRows.has(o.order_line_id));
    const res = await fetch(`${API_URL}/api/transport/planning/bulk-update`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ date: selectedDate, orderLineIds, haulageJobIds, changes })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) throw new Error(data.error || 'Could not update the rows');
    if ((data.customer_collection_skipped || []).length) {
      const list = data.customer_collection_skipped;
      setAlertDialog({
        open: true,
        title: 'Customer Collection left alone',
        message: `${list.join(', ')} ${list.length === 1 ? 'is' : 'are'} Customer Collection — the customer collects, so no haulier, driver or vehicle was set on ${list.length === 1 ? 'it' : 'them'}. Any time was still applied.`,
        type: 'info'
      });
    }
    setBulkEditOpen(false);
    setSelectedRows(new Set());
    setLastAutoSaved(new Date());
    fetchOrders();
    // An outside haulier: raise the PO-H? (Customer Collection rows were left alone.)
    if ('haulier' in changes) {
      const skipped = new Set(data.customer_collection_skipped || []);
      outsidePoh.afterHaulierChange(editedRows.filter(o => !o.is_customer_collection && !skipped.has(o.order_number)), changes.haulier);
    }
  };
  const bulkEditBlocked = !canEdit ? noEditReason
    : isLocked ? 'This day is locked' : null;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/transport/planning/orders?date=${selectedDate}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        // Stock rows (supplied off our own stock) have no PO — give them a
        // synthetic key so the PO-keyed handlers stay unique per row.
        // A redelivery row likewise, by its order line (utils/planningRows boardKeyOf).
        setOrders((data.orders || []).map(o => (o.is_stock_line || o.is_redelivery
          ? { ...o, purchase_order_id: boardKeyOf(o) }
          : o)));
        setIsSnapshot(data.isSnapshot === true);
        setLorryDayState(data.lorryDay || { enabled: false });
      } else {
        console.error('Failed to fetch orders:', data.error);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Per-driver "Unknown" vehicle payloads for the selected day.
  const fetchUnknownPayloads = async (date) => {
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/unknown-payloads?date=${date}`, { credentials: 'include' });
      const data = await res.json();
      setUnknownPayloads(data.success && data.payloads ? data.payloads : {});
    } catch (error) {
      console.error('Failed to load unknown vehicle payloads:', error);
      setUnknownPayloads({});
    }
  };

  // Set (or clear) a driver's "Unknown" vehicle payload for the day. Optimistic —
  // the load summaries re-analyse the moment state lands.
  const setUnknownPayload = async (driverName, payload) => {
    const driver = (driverName || '').trim();
    const value = (payload === '' || payload == null) ? null : Number(payload);
    setUnknownPayloads(prev => {
      const next = { ...prev };
      if (value == null) delete next[driver]; else next[driver] = value;
      return next;
    });
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/unknown-payload`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, driverName: driver, payload: value })
      });
      const data = await res.json();
      if (data.success) setLastAutoSaved(new Date());
      else { console.error('Failed to save unknown payload:', data.error); fetchUnknownPayloads(selectedDate); }
    } catch (error) {
      console.error('Error saving unknown payload:', error);
      fetchUnknownPayloads(selectedDate);
    }
  };

  const updateHaulier = async (orderLineId, haulier) => {
    // Owner-driver: a haulier with a same-named driver (Taylors → Taylors)
    // brings that driver with it, saved in the same request as the haulier.
    const matchDriver = haulier
      ? drivers.find(d => d.name.trim().toLowerCase() === haulier.trim().toLowerCase())
      : null;
    const changes = { haulier: haulier || null, ...(matchDriver ? { driver: matchDriver.name } : {}) };
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/bulk-update`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, orderLineIds: [orderLineId], changes, source: 'Transport planning' })
      });
      const data = await response.json();
      if (data.success) {
        setOrders(prev => prev.map(order =>
          order.order_line_id === orderLineId ? { ...order, ...changes, has_changes: false } : order
        ));
        setLastAutoSaved(new Date());
        outsidePoh.afterHaulierChange(orders.filter(o => o.order_line_id === orderLineId), changes.haulier);
      } else {
        console.error('Failed to update haulier:', data.error);
      }
    } catch (error) {
      console.error('Error updating haulier:', error);
    }
  };

  const updateDriver = async (orderLineId, driver) => {
    // The driver brings their haulier and vehicle with them — worked out
    // first, then saved in ONE request, so the history reads as one change
    // ("driver, haulier, vehicle") rather than three entries a moment apart.
    // A driver's default haulier is their sole linked one; their default
    // vehicle, else "Unknown" for an outside haulier's driver (payload set
    // later) and nothing for ours. The user can still override afterwards.
    const driverRecord = drivers.find(d => d.name === driver);
    const defaultReg = driverRecord?.default_vehicle_registration;
    const linkedHauliers = driverRecord?.hauliers || [];
    const changes = { driver: driver || null };
    if (driver && linkedHauliers.length === 1) changes.haulier = linkedHauliers[0].name;
    if (driver && defaultReg) {
      changes.vehicle = defaultReg;
    } else if (driver) {
      const row = orders.find(o => o.order_line_id === orderLineId);
      const haulierName = changes.haulier || row?.haulier || '';
      if (haulierName && !isExcelHaulier(haulierName)) changes.vehicle = UNKNOWN_VEHICLE;
    }
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/bulk-update`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, orderLineIds: [orderLineId], changes, source: 'Transport planning' })
      });
      const data = await response.json();
      if (data.success) {
        setOrders(prev => prev.map(order =>
          order.order_line_id === orderLineId ? { ...order, ...changes, has_changes: false } : order
        ));
        setLastAutoSaved(new Date());
        // The driver brought their haulier with them.
        if ('haulier' in changes) outsidePoh.afterHaulierChange(orders.filter(o => o.order_line_id === orderLineId), changes.haulier);
      } else {
        console.error('Failed to update driver:', data.error);
      }
    } catch (error) {
      console.error('Error updating driver:', error);
    }
  };

  const updateVehicle = async (orderLineId, vehicle) => {
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/update-vehicle`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderLineId, vehicle })
      });
      const data = await response.json();
      if (data.success) {
        setOrders(prev => prev.map(order =>
          order.order_line_id === orderLineId ? { ...order, vehicle, has_changes: false } : order
        ));
        setLastAutoSaved(new Date());
      } else {
        console.error('Failed to update vehicle:', data.error);
      }
    } catch (error) {
      console.error('Error updating vehicle:', error);
    }
  };

  // ── Vehicle load analysis (payload + compartment fit per assigned vehicle) ──
  // Groups the day's rows by their assigned vehicle in board order and runs the
  // deterministic checker. Loads split by collection TIME only (a load may
  // collect from several suppliers/mills). Top row = first drop = REAR-most
  // compartment. The result is ordered to mirror the board: grouped by driver,
  // each driver's runs in run order, drivers in table order (top-most first).
  // Board rows and load cards carry driver *names* (orders.order_lines.driver is
  // a varchar, not an FK), so linking one needs a name -> id lookup off the
  // drivers list we already fetch.
  const driverIdByName = useMemo(() => {
    const map = {};
    drivers.forEach(d => { if (d && d.name) map[d.name.toLowerCase()] = d.id; });
    return map;
  }, [drivers]);
  const driverIdFor = (name) => (name ? driverIdByName[String(name).toLowerCase()] : null);

  const vehicleLoads = useMemo(() => {
    // Group by assigned vehicle in board order. The "Unknown" vehicle is shared
    // across drivers, so it sub-groups by driver — each driver gets their own
    // Unknown card + payload.
    const groups = new Map(); // groupKey -> { reg, driver, rows }
    // Our deliveries on the day only (utils/selfBilledPo ourDeliveryRows): the
    // customer collects a Customer Collection order, and an order on one of
    // its option dates that is not its delivery date is planned on another
    // day (utils/planningDay) — neither is on a load of ours here.
    for (const o of ourDeliveryRows(orders)) {
      const reg = (o.vehicle || '').trim();
      if (!reg) continue;
      const driver = (o.driver || '').trim();
      const key = reg === UNKNOWN_VEHICLE ? `${UNKNOWN_VEHICLE}::${driver}` : reg;
      if (!groups.has(key)) groups.set(key, { reg, driver, rows: [] });
      groups.get(key).rows.push(o);
    }
    const out = [];
    for (const [, g] of groups) {
      let spec;
      if (g.reg === UNKNOWN_VEHICLE) {
        // Synthetic spec: just a payload (per driver, per day), no compartments —
        // so the checker reports payload utilisation only, not compartment fit.
        // Payload is left UNDEFINED (not null) when unset: Number(null) is 0,
        // which usablePayload would treat as a real 0T payload, but Number(undefined)
        // is NaN → null, i.e. "no payload set".
        const p = unknownPayloads[g.driver];
        spec = {
          registration: UNKNOWN_VEHICLE,
          payload: (p == null || p === '') ? undefined : Number(p),
          vehicle_type: null, has_moffett: false,
          has_moveable_doors: false, compartment_count: 0
        };
      } else {
        spec = vehicleSpecs[g.reg];
        if (!spec) continue; // unknown/archived vehicle — nothing to check against
      }
      const rows = g.rows;
      // A vehicle does one or more loads/day (collect → deliver → collect …).
      const loads = segmentLoads(rows);
      loads.forEach((ld, idx) => {
        const lineTonnes = ld.rows.map(o => {
          const t = Number(o.line_tonnes);
          return Number.isFinite(t) ? t : (Number(o.quantity) || 0);
        });
        // Product key per drop — enables the same-product compartment-mixing rule.
        const products = ld.rows.map(o => o.product_code || o.product_name || null);
        // User-chosen door layout for this load (stored per PO, shared across the
        // load); null = use the auto-picked tightest layout.
        const overrideDoors = ld.rows.map(o => o.door_config).find(d => Array.isArray(d)) || null;
        const poIds = [...new Set(ld.rows.map(o => o.purchase_order_id).filter(Boolean))];
        // A load can now span several suppliers/mills — surface the distinct set.
        const suppliers = [...new Set(ld.rows
          .map(o => (o.supplier_name || o.contact_name || o.supplier_account_no || '').trim())
          .filter(Boolean))];
        out.push({
          ...analyzeVehicleLoad(spec, lineTonnes, products, overrideDoors),
          rows: ld.rows,
          supplier: suppliers.join(', ') || null,
          time: ld.time,
          times: ld.times,
          timeLabel: loadTimeLabel(ld),
          driver: (ld.rows.find(r => r.driver && r.driver.trim()) || {}).driver || null,
          loadIndex: idx,
          loadCount: loads.length,
          poIds,
          vehicle: spec,
          isUnknown: g.reg === UNKNOWN_VEHICLE,
          unknownDriver: g.reg === UNKNOWN_VEHICLE ? g.driver : null
        });
      });
    }
    // Order the cards to mirror the board rather than by severity: a driver's
    // runs sit together in run order, and the drivers follow the table from the
    // top down. A load's position IS its top-most row, and loads are segmented
    // out of board order, so ordering on that gives run order for free (and
    // still holds if a driver takes more than one vehicle in a day).
    const rowIndex = new Map();
    orders.forEach((o, i) => rowIndex.set(o.order_line_id, i));
    const posOf = (l) => Math.min(...l.rows.map(r => {
      const i = rowIndex.get(r.order_line_id);
      return i == null ? Infinity : i;
    }));
    const loadPos = new Map(out.map(l => [l, posOf(l)]));
    const driverKey = (l) => (l.driver || '').trim();
    // Each driver is pinned to their top-most row on the board.
    const driverPos = new Map();
    for (const l of out) {
      const k = driverKey(l);
      const p = loadPos.get(l);
      if (!driverPos.has(k) || p < driverPos.get(k)) driverPos.set(k, p);
    }
    out.sort((a, b) =>
      (driverPos.get(driverKey(a)) - driverPos.get(driverKey(b)))
      || (loadPos.get(a) - loadPos.get(b)));
    return out;
  }, [orders, vehicleSpecs, unknownPayloads]);

  // Open load (drill-down modal). Keyed by the load's stable identity so the open
  // modal re-resolves to the freshly-analysed load after a config change (rather
  // than holding a stale snapshot) — it refreshes in place instead of closing.
  const [loadModal, setLoadModal] = useState({ open: false, key: null });
  // Driver is in the key so several drivers' "Unknown" loads (all registration
  // 'Unknown') stay distinct.
  const loadKey = (l) => `${l.registration}::${(l.driver || '')}::${l.loadIndex}`;
  // Row → compartments it loads into, numbered from the cab (1) to the rear.
  const compartmentsFor = useMemo(() => compartmentsByRow(vehicleLoads), [vehicleLoads]);
  // Row → its drop number within its load (the "D#" badge by the time).
  const dropNumbers = useMemo(() => dropNumbersByRow(vehicleLoads), [vehicleLoads]);

  // ── Lorry day planning (switched on) ───────────────────────────────────
  // Each order is collected at its collection time and dropped at its drop
  // time; each lorry's day is worked out here by utils/lorryDay — the code
  // the server prints the paperwork from — from what the planners entered.
  // Nothing is moved or refused over a check: they are guidance.
  const lorryDayOn = !!lorryDayState.enabled;
  const vehicleSpecFor = (registration, driver) => (String(registration || '').trim() === UNKNOWN_VEHICLE
    ? unknownVehicleSpec(unknownPayloads[String(driver || '').trim()])
    : (vehicleSpecs[String(registration || '').trim()] || null));
  const lorryDays = useMemo(() => {
    if (!lorryDayOn) return [];
    try {
      const linePlans = {};
      for (const o of orders) if (o.line_plan) linePlans[String(o.order_line_id)] = o.line_plan;
      return buildLorryDays({
        rows: orders,
        vehicleFor: (key, { registration, driver }) => vehicleSpecFor(registration, driver),
        linePlans,
        lorryDayFor: (k) => (lorryDayState.lorryDays && lorryDayState.lorryDays[k]) || null
      });
    } catch (error) {
      // Never take the board down over the plan.
      console.warn('Lorry day planning: could not work the days out', error);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lorryDayOn, orders, vehicleSpecs, unknownPayloads, lorryDayState]);
  const lorryDayByKey = useMemo(() => new Map(lorryDays.map(d => [d.lorryKey, d])), [lorryDays]);
  const planByRow = useMemo(() => rowPlanMap(lorryDays), [lorryDays]);
  const lorryDayReadOnly = !canEdit ? noEditReason
    : isPastDate ? 'This day has passed — its plan is kept as it was'
    : isLocked ? 'This day is locked — unlock it to change the plan'
    : isSnapshot ? 'This plan can only be viewed here'
    : lorryDayState.ready === false ? 'Lorry day planning is not set up on this database yet (migration transport_line_plans.sql)'
    : null;
  // ✓ planned · ◐ part-planned · ○ unplanned — the one rule (utils/lorryDay).
  const statusOf = (o) => rowPlanStatus(o, o.line_plan, bodyOf(vehicleSpecFor(o.vehicle, o.driver)));
  const statusTitle = (st) => (st.carrier ? `${st.carrier} — planned (not on one of our lorries)`
    : st.status === 'planned' ? 'Planned: vehicle, collection time, drop time and compartments'
    : st.status === 'part' ? `Part-planned — still to do: ${st.missing.join(', ')}`
    : st.status === 'unplanned' ? 'Not planned — put it on a vehicle' : 'Planned on another of its option dates');

  // Typed on the board: a drop time, or a planner's note (one line plan).
  const saveLinePlan = async (order, patch) => {
    const id = order.order_line_id;
    const before = order.line_plan || null;
    setOrders(prev => prev.map(o => (o.order_line_id === id
      ? { ...o, line_plan: normaliseLinePlan({ ...(o.line_plan || {}), ...patch }) } : o)));
    try {
      await saveDropTimes(selectedDate, [{ orderLineId: id, ...patch }]);
      setLastAutoSaved(new Date());
    } catch (error) {
      setOrders(prev => prev.map(o => (o.order_line_id === id ? { ...o, line_plan: before } : o)));
      throw error;
    }
  };
  const saveDropTime = (order, dropTime) => saveLinePlan(order, { dropTime }).catch(error => {
    setAlertDialog({ open: true, title: 'Drop time not saved', message: error.message, type: 'error' });
  });

  // One lorry's day from the Lorry day window, saved as it changes (the
  // window debounces and says when a save fails — this throws the reason).
  // The board behind follows at once; it is read again when the window
  // closes (a collection time is per PO, so rows on other lorries may move).
  const saveLorryDayDraft = async (date, lorryKey, payload, opts = {}) => {
    await saveLorryDay(date, lorryKey, payload, opts);
    lorryDaySavedRef.current = true;
    if (date !== selectedDateRef.current) return true;
    const lines = payload.lines || {};
    const times = payload.collectTimes || {};
    setOrders(prev => prev.map(o => {
      const id = String(o.order_line_id);
      if (!lines[id] && !(id in times)) return o;
      let n = o;
      if (lines[id]) n = { ...n, line_plan: normaliseLinePlan({ ...(o.line_plan || {}), ...lines[id] }) };
      if (id in times) n = { ...n, scheduled_time: times[id] };
      return n;
    }));
    if (payload.lorryDay !== undefined) {
      setLorryDayState(s => ({ ...s, lorryDays: { ...(s.lorryDays || {}), [lorryKey]: payload.lorryDay } }));
    }
    setLastAutoSaved(new Date());
    return true;
  };

  // The board's groups (utils/boardGroups groupOf): one per haulier + driver
  // + vehicle; Unassigned first; rows someone else carries in groups of their
  // own at the end. The office summary prints the same groups.

  // Drag rows (a selected block moves together) onto another group: they
  // take its haulier, driver and vehicle — one bulk-update, as the bulk edit.
  const startAssignDrag = (e, order) => {
    if (e.target.closest && e.target.closest('.col-select, input, button, .searchable-dropdown, .dropdown')) { e.preventDefault(); return; }
    assignDragRef.current = (selectedRows.has(order.order_line_id) && selectedRows.size > 1)
      ? orders.filter(o => selectedRows.has(o.order_line_id)).map(o => o.order_line_id)
      : [order.order_line_id];
    if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', 'rows'); }
  };
  const overGroup = (e, g) => {
    if (!assignDragRef.current) return;
    e.preventDefault();
    updateAutoScroll(e.clientY);
    if (dropTargetGroup !== g.key) setDropTargetGroup(g.key);
  };
  const endAssignDrag = () => {
    assignDragRef.current = null;
    setDropTargetGroup(null);
    setDropTargetRow(null);
    stopAutoScroll();
  };
  // Rows dragged over a row of their OWN group re-order the group (Cam, 22 Oct
  // 2026: "visual only — still goes on times really, just if they want to
  // visually plan before inputting actual times"). Over another group's row,
  // the group is the target as before (assign).
  const sameGroupDrag = (g) => {
    const ids = assignDragRef.current;
    if (!ids || !ids.length || g.kind === 'other') return false;
    return orders.every(o => !ids.includes(o.order_line_id) || groupOf(o).key === g.key);
  };
  const overRow = (e, g, order) => {
    if (!assignDragRef.current) return;
    if (!sameGroupDrag(g)) { overGroup(e, g); return; }
    e.preventDefault();
    updateAutoScroll(e.clientY);
    if (dropTargetGroup) setDropTargetGroup(null);
    if (assignDragRef.current.includes(order.order_line_id)) { if (dropTargetRow) setDropTargetRow(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    if (!dropTargetRow || dropTargetRow.id !== order.order_line_id || dropTargetRow.after !== after) {
      setDropTargetRow({ id: order.order_line_id, after });
    }
  };
  const dropOnRow = (e, g, order) => {
    if (!sameGroupDrag(g)) { dropOnGroup(e, g); return; }
    e.preventDefault();
    const ids = assignDragRef.current;
    const target = dropTargetRow;
    endAssignDrag();
    if (!ids || !ids.length || planFixed || !canEdit || !target || ids.includes(target.id)) return;
    const moving = new Set(ids);
    const block = orders.filter(o => moving.has(o.order_line_id));
    const rest = orders.filter(o => !moving.has(o.order_line_id));
    const at = rest.findIndex(o => o.order_line_id === target.id);
    if (at < 0) return;
    const insertAt = target.after ? at + 1 : at;
    const next = [...rest.slice(0, insertAt), ...block, ...rest.slice(insertAt)];
    if (next.every((o, i) => o.order_line_id === orders[i].order_line_id)) return;
    persistBoardOrder(next);
    setLastAutoSaved(new Date());
  };
  const dropOnGroup = async (e, g) => {
    e.preventDefault();
    const ids = assignDragRef.current;
    endAssignDrag();
    if (!ids || !ids.length || planFixed || !canEdit) return;
    if (g.kind === 'other') {
      setAlertDialog({ open: true, title: `${g.label} is set on the order`, message: `Rows go in ${g.label} by the order's delivery terms, not by dragging — change them on the order.`, type: 'info' });
      return;
    }
    const moving = orders.filter(o => ids.includes(o.order_line_id) && !otherCarrierOf(o) && groupOf(o).key !== g.key);
    if (!moving.length) return;
    const changes = g.kind === 'unassigned'
      ? { haulier: null, driver: null, vehicle: null }
      : { haulier: g.haulier || null, driver: g.driver || null, vehicle: g.vehicle || null };
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/bulk-update`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, orderLineIds: moving.map(o => o.order_line_id), changes, source: 'Transport planning' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not move the rows');
      setSelectedRows(new Set());
      setLastAutoSaved(new Date());
      fetchOrders();
      const skipped = new Set(data.customer_collection_skipped || []);
      outsidePoh.afterHaulierChange(moving.filter(o => !o.is_customer_collection && !skipped.has(o.order_number)), changes.haulier);
    } catch (error) {
      setAlertDialog({ open: true, title: 'Rows not moved', message: error.message, type: 'error' });
    }
  };

  const activeLoad = loadModal.open ? vehicleLoads.find(l => loadKey(l) === loadModal.key) : null;

  /** Save a new board order (send to top, group, auto-group, move to…). */
  const persistBoardOrder = (newOrders) => {
    setOrders(newOrders);
    updateOrderSequence(newOrders);
  };


  const updateScheduledTime = async (purchaseOrderId, scheduledTime) => {
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/update-scheduled-time`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: selectedDate,
          // Stock rows have no PO — they're keyed by their order line instead.
          ...(isStockKey(purchaseOrderId)
            ? { orderLineId: stockLineIdOf(purchaseOrderId) }
            : { purchaseOrderId }),
          scheduledTime
        })
      });
      const data = await response.json();
      if (data.success) {
        // Update local state - also clear has_changes since transport planning was just updated
        setOrders(prev => prev.map(order =>
          order.purchase_order_id === purchaseOrderId ? { ...order, scheduled_time: scheduledTime, has_changes: false } : order
        ));
        setLastAutoSaved(new Date());
      } else {
        console.error('Failed to update scheduled time:', data.error);
      }
    } catch (error) {
      console.error('Error updating scheduled time:', error);
    }
  };

  // A collection time typed on the board. With lorry day planning on, it also
  // fills the drop time of each of the PO's rows whose drop time is blank or
  // earlier (Cam, 2026-10-15) — still the planner's to type over.
  const commitCollectionTime = (order, time) => {
    updateScheduledTime(order.purchase_order_id, time);
    if (!lorryDayOn || !time || lorryDayReadOnly) return;
    const fills = orders
      .filter(o => o.purchase_order_id === order.purchase_order_id && !o.no_line_plan && !otherCarrierOf(o) && !isCandidateRow(o))
      .map(o => ({ o, dropTime: dropTimeAfterCollect(normaliseLinePlan(o.line_plan).dropTime, time) }))
      .filter(f => f.dropTime);
    if (!fills.length) return;
    const ids = new Set(fills.map(f => f.o.order_line_id));
    const before = new Map(fills.map(f => [f.o.order_line_id, f.o.line_plan || null]));
    setOrders(prev => prev.map(o => (ids.has(o.order_line_id)
      ? { ...o, line_plan: normaliseLinePlan({ ...(o.line_plan || {}), dropTime: fills.find(f => f.o.order_line_id === o.order_line_id).dropTime }) } : o)));
    saveDropTimes(selectedDate, fills.map(f => ({ orderLineId: f.o.order_line_id, dropTime: f.dropTime })))
      .then(() => setLastAutoSaved(new Date()))
      .catch(error => {
        setOrders(prev => prev.map(o => (before.has(o.order_line_id) ? { ...o, line_plan: before.get(o.order_line_id) } : o)));
        setAlertDialog({ open: true, title: 'Drop time not filled in', message: error.message, type: 'error' });
      });
  };

  const dismissModified = async (order) => {
    // Call API to update acknowledged_at timestamp for this specific row
    if (order.sequence_id) {
      try {
        await fetch(`${API_URL}/api/audit/transport/${order.sequence_id}/acknowledge`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
      } catch (error) {
        console.error('Failed to acknowledge changes:', error);
      }
    }
    // Update local state
    setOrders(orders.map(o =>
      o.purchase_order_id === order.purchase_order_id ? { ...o, has_changes: false, acknowledged_at: new Date().toISOString() } : o
    ));
    // Keep the date-selector warning dots in step with the acknowledgment.
    fetchDatesWithChanges();
  };

  const toggleChangeEntry = (idx) => {
    setExpandedChanges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };

  const handleShowChanges = async (order, hasUnacknowledged = true) => {
    setExpandedChanges(new Set());
    setChangesModal({ open: true, order, changes: [], loading: true, hasUnacknowledged });
    try {
      // If viewing history (no unacknowledged changes), fetch all changes; otherwise fetch since last acknowledgment
      const since = hasUnacknowledged ? (order.acknowledged_at || order.updated_at || '') : '';
      const url = since
        ? `${transportChangesUrl(order)}?since=${encodeURIComponent(since)}`
        : transportChangesUrl(order);
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      const changes = Array.isArray(data) ? data : [];
      // Expand all entries by default for unacknowledged changes
      if (hasUnacknowledged && changes.length > 0) {
        setExpandedChanges(new Set(changes.map((_, i) => i)));
      }
      setChangesModal(prev => ({ ...prev, changes, loading: false }));
    } catch (error) {
      console.error('Failed to fetch changes:', error);
      setChangesModal(prev => ({ ...prev, changes: [], loading: false }));
    }
  };

  const formatChangeTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Format change values for display (dates, numbers, etc.)
  const formatChangeValue = (value, fieldName) => {
    if (value === null || value === undefined) return '-';
    // Format dates
    if (fieldName === 'delivery_date' || (fieldName && fieldName.includes('date'))) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        // Date-only strings or midnight timestamps — show date only
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
        const isMidnight = date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
        if (isDateOnly || isMidnight) {
          return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      }
    }
    // Format numbers (quantity, price)
    if (fieldName === 'quantity' || fieldName === 'unit_price') {
      const num = parseFloat(value);
      if (!isNaN(num)) return fieldName === 'quantity' ? formatQuantity(num) : num.toFixed(2);
    }
    return String(value);
  };

  const updateOrderSequence = async (newOrders) => {
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/update-sequence`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: selectedDate,
          // Only planned rows carry a sequence for this day — option-date
          // candidate rows (shown here but planned elsewhere) are excluded so
          // they never get a transport_sequence row on this date.
          // Stock rows persist their sequence against the order line
          // (utils/planningRows boardSequence).
          orderSequence: boardSequence(newOrders)
        })
      });
      const data = await response.json();
      if (data.success) {
        setLastAutoSaved(new Date());
      } else {
        console.error('Failed to update sequence:', data.error);
        // Revert on error
        fetchOrders();
      }
    } catch (error) {
      console.error('Error updating sequence:', error);
      fetchOrders();
    }
  };

  // Apply an alternative compartment configuration to a load: re-sequence that
  // load's drops to the config's drop order, leaving every other row in place,
  // then persist (same mechanism as drag-drop). dropOrder is line indices,
  // rear-first (= first drop first).
  // Persist a load's chosen door layout (override) for every PO in the load, and
  // mirror it into local state so the load re-analyses immediately. doors = null
  // clears the override (reverts to the auto-picked layout).
  const persistLoadDoorConfig = async (load, doors) => {
    const poIds = load?.poIds && load.poIds.length
      ? load.poIds
      : [...new Set((load?.rows || []).map(r => r.purchase_order_id).filter(Boolean))];
    if (!poIds.length) return;
    setOrders(prev => prev.map(o =>
      poIds.includes(o.purchase_order_id) ? { ...o, door_config: doors } : o));
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/update-door-config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: selectedDate,
          purchaseOrderIds: poIds.filter(id => !isStockKey(id)),
          orderLineIds: poIds.filter(isStockKey).map(stockLineIdOf),
          doorConfig: doors
        })
      });
      const data = await res.json();
      if (data.success) setLastAutoSaved(new Date());
      else { console.error('Failed to save door config:', data.error); fetchOrders(); }
    } catch (error) {
      console.error('Error saving door config:', error);
      fetchOrders();
    }
  };

  // Apply a configuration chosen in the load modal: optionally re-sequence the
  // load's drops (dropOrder) and/or set its door layout (doors). Either may be
  // omitted — doors null leaves the door override untouched.
  const applyLoadConfig = (load, dropOrder, doors) => {
    if (!load?.rows) return;
    if (Array.isArray(dropOrder) && !dropOrder.every((v, i) => v === i)) {
      const reordered = dropOrder.map(i => load.rows[i]).filter(Boolean);
      if (reordered.length === load.rows.length) {
        const positions = load.rows
          .map(r => orders.findIndex(o => o.order_line_id === r.order_line_id))
          .filter(p => p >= 0)
          .sort((a, b) => a - b);
        if (positions.length === reordered.length) {
          const newOrders = [...orders];
          positions.forEach((pos, k) => { newOrders[pos] = reordered[k]; });
          setOrders(newOrders);
          updateOrderSequence(newOrders);
        }
      }
    }
    if (doors !== undefined) persistLoadDoorConfig(load, doors);
  };

  // Consolidated Customer/Order notes for a row (account + sales-order level).
  const openNotes = (order) => setNotesModal({ open: true, order });
  const orderHasNotes = (order) =>
    !!((order.customer_notes && order.customer_notes.trim()) || (order.order_notes && order.order_notes.trim()));

  // ── Row selection helpers ──────────────────────────────────────────────
  const toggleRowSelected = (order) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(order.order_line_id)) next.delete(order.order_line_id);
      else next.add(order.order_line_id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedRows(prev =>
      prev.size === orders.length && orders.length > 0
        ? new Set()
        : new Set(orders.map(o => o.order_line_id))
    );
  };

  // Click-drag across the checkboxes to paint a selection (same UX as Search
  // Invoices). setChecked is driven per-row by the shared useDragSelect hook.
  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedRows(prev => {
        const next = new Set(prev);
        if (checked) next.add(id); else next.delete(id);
        return next;
      });
    }
  });

  // ── Reorder actions (operate on an explicit set of order_line_ids) ──────
  // Reposition the given rows as a contiguous block, preserving their relative
  // order, inserting at `insertAt` within the non-block rows.
  const reorderIds = (idSet, insertAt) => {
    if (planFixed || !idSet || idSet.size === 0) return;
    const blockRows = orders.filter(o => idSet.has(o.order_line_id));
    if (!blockRows.length) return;
    const rest = orders.filter(o => !idSet.has(o.order_line_id));
    const clamped = Math.max(0, Math.min(insertAt, rest.length));
    const newOrders = [...rest.slice(0, clamped), ...blockRows, ...rest.slice(clamped)];
    persistBoardOrder(newOrders);
  };

  // Send the given rows to the top (grouped, order preserved).
  const sendIdsToTop = (idSet) => reorderIds(idSet, 0);

  // Group the given rows together under the top-most of them.
  const groupIds = (idSet) => {
    if (!idSet || idSet.size < 2) return;
    const firstIdx = orders.findIndex(o => idSet.has(o.order_line_id));
    if (firstIdx < 0) return;
    const before = orders.slice(0, firstIdx).filter(o => !idSet.has(o.order_line_id)).length;
    reorderIds(idSet, before);
  };

  // Combine run: make the selected rows ONE run for one vehicle and group them
  // together. A run is one load whatever its rows' times (segmentLoads), so a
  // driver can collect at 06:00 at one mill and 07:00 at the next en route, then
  // deliver the lot. Each row keeps its own time unless one is entered here, in
  // which case they all take it. The vehicle summaries are a useMemo over
  // `orders`, so they re-analyse as soon as state lands.
  const applyCombineRun = async () => {
    if (planFixed) { setCombineRunModal({ open: false, value: '', targetIds: null }); return; }
    const idSet = combineRunModal.targetIds instanceof Set
      ? combineRunModal.targetIds
      : new Set(combineRunModal.targetIds || []);
    if (idSet.size === 0) { setCombineRunModal({ open: false, value: '', targetIds: null }); return; }

    const typed = String(combineRunModal.value || '').trim();
    const normalized = typed ? normalizeTimeValue(typed) : null;
    if (typed && !/^\d{2}:\d{2}$/.test(normalized)) {
      setAlertDialog({
        open: true,
        title: 'Invalid Time',
        message: 'Enter a collection time — e.g. 9, 14:30 or 2pm — or leave it blank to keep each row\'s own time.',
        type: 'warning'
      });
      return;
    }

    // A Customer Collection order is in no vehicle's run (the server refuses it
    // too), nor is an option-date candidate (planned on another day).
    const blockRows = ourDeliveryRows(orders).filter(o => idSet.has(o.order_line_id));
    if (!blockRows.length) { setCombineRunModal({ open: false, value: '', targetIds: null }); return; }

    // A run belongs to one vehicle — warn rather than silently not combining.
    const regs = [...new Set(blockRows.map(o => (o.vehicle || '').trim()).filter(Boolean))];
    if (regs.length > 1) {
      setAlertDialog({
        open: true,
        title: 'Different Vehicles',
        message: `These rows are on ${regs.length} different vehicles (${regs.join(', ')}). A run belongs to one vehicle — assign them to the same vehicle first.`,
        type: 'warning'
      });
      return;
    }

    // Group + stamp the run (and any shared time) in a single state update so
    // the summaries don't flash through an intermediate arrangement.
    const runKey = newRunKey();
    const rest = orders.filter(o => !idSet.has(o.order_line_id));
    const firstIdx = orders.findIndex(o => idSet.has(o.order_line_id));
    const insertAt = orders.slice(0, firstIdx).filter(o => !idSet.has(o.order_line_id)).length;
    const stamped = blockRows.map(o => ({ ...o, ...(normalized ? { scheduled_time: normalized } : {}), has_changes: false }));
    const newOrders = withRunKey([...rest.slice(0, insertAt), ...stamped, ...rest.slice(insertAt)], blockRows, runKey);
    setOrders(newOrders);
    setCombineRunModal({ open: false, value: '', targetIds: null });

    // Persist the new order, the run, then (if one was entered) the shared time.
    updateOrderSequence(newOrders);
    const poIds = [...new Set(blockRows.map(r => r.purchase_order_id).filter(Boolean))];
    try {
      await saveRun(selectedDate, blockRows, runKey);
      if (normalized) {
        await Promise.all(poIds.map(poId =>
          fetch(`${API_URL}/api/transport/planning/update-scheduled-time`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: selectedDate,
            ...(isStockKey(poId) ? { orderLineId: stockLineIdOf(poId) } : { purchaseOrderId: poId }),
            scheduledTime: normalized
          })
        })
        ));
      }
      setLastAutoSaved(new Date());
    } catch (error) {
      console.error('Failed to combine run:', error);
      setAlertDialog({ open: true, title: 'Could not combine', message: error.message, type: 'error' });
      fetchOrders();
    }
  };

  // Split from run: the rows go back to being grouped by their times.
  const splitFromRun = async (rows) => {
    const inRun = (rows || []).filter(o => o.run_key);
    if (planFixed || !inRun.length) return;
    setOrders(prev => withRunKey(prev, inRun, null));
    try {
      await saveRun(selectedDate, inRun, null);
      setLastAutoSaved(new Date());
    } catch (error) {
      console.error('Failed to split run:', error);
      setAlertDialog({ open: true, title: 'Could not split', message: error.message, type: 'error' });
      fetchOrders();
    }
  };

  // Move to…: group the target rows and drop them directly beneath the row
  // currently showing the (1-based) number entered in the modal.
  const applyMoveTo = () => {
    const idSet = moveToModal.targetIds instanceof Set
      ? moveToModal.targetIds
      : new Set(moveToModal.targetIds || []);
    // Row numbers count planned rows only (option-date candidates are numberless).
    const plannedList = orders.filter(o => !isOptionCandidate(o));
    const n = parseInt(moveToModal.value, 10);
    if (!Number.isFinite(n) || n < 1 || n > plannedList.length) {
      setAlertDialog({
        open: true,
        title: 'Invalid Row Number',
        message: `Please enter a row number between 1 and ${plannedList.length}.`,
        type: 'warning'
      });
      return;
    }
    if (idSet.size === 0) { setMoveToModal({ open: false, value: '', targetIds: null }); return; }
    // Insert the block directly beneath the n-th planned row.
    const targetRow = plannedList[n - 1];
    const targetIdx = orders.findIndex(o => o.order_line_id === targetRow.order_line_id);
    const insertAt = orders.slice(0, targetIdx + 1).filter(o => !idSet.has(o.order_line_id)).length;
    reorderIds(idSet, insertAt);
    setMoveToModal({ open: false, value: '', targetIds: null });
  };

  // Auto-group: reorder the whole table so rows sit together by driver, keeping
  // each driver's rows in their existing order (group order = first appearance;
  // unassigned rows form their own group).
  const autoGroupByDriver = () => {
    if (planFixed) return;
    const groups = [];
    const byKey = new Map();
    for (const o of orders) {
      // NUL-prefixed so the unassigned bucket can't collide with a driver
      // actually named "unassigned". Keep the \u0000 escape: a literal NUL
      // byte here makes plain grep treat the whole file as binary and
      // silently report no matches anywhere in it.
      const key = (o.driver || '').trim() || '\u0000unassigned';
      if (!byKey.has(key)) { const g = []; byKey.set(key, g); groups.push(g); }
      byKey.get(key).push(o);
    }
    const newOrders = groups.flat();
    persistBoardOrder(newOrders);
  };

  // Move the given rows' customer orders (and their linked POs) to another
  // delivery date. delivery_date lives on the order, so this moves the whole
  // order(s) the rows belong to; the rows then leave the current day's board.
  const openMoveDate = (rows) => {
    const orderIds = [...new Set(rows.map(o => o.id).filter(Boolean))];
    // Collect the target orders' option dates (deduped, in preference order) to
    // offer them as quick picks below the date picker.
    const seen = new Set();
    const optionDates = [];
    rows.forEach(o => (Array.isArray(o.option_dates) ? o.option_dates : []).forEach(od => {
      if (od && od.date && !seen.has(od.date)) { seen.add(od.date); optionDates.push(od); }
    }));
    optionDates.sort((a, b) => (a.preference - b.preference) || String(a.date).localeCompare(String(b.date)));
    setMoveDateModal({ open: true, value: '', orderIds, count: rows.length, optionDates });
  };
  /*
   * Moving a delivery date moves the dates on the order's POs — and a PO the
   * supplier (or haulier) already holds now says the wrong day. The server
   * names those (`resend`); the user is asked to send the revised version, and
   * each email says it is a new delivery date (SendPOEmailModal textFor).
   */
  // Paperwork send/history (PaperworkSendModal / PaperworkHistoryModal).
  const [officeEmail, setOfficeEmail] = useState('admin@excel-agri.com');
  const [historyModal, setHistoryModal] = useState({ open: false, kind: null });
  // Plan History: the whole day — changes, moves, board actions, emails.
  const [planHistoryOpen, setPlanHistoryOpen] = useState(false);
  // Which tab Plan History opens on — Emails, unless asked otherwise.
  const [planHistoryTab, setPlanHistoryTab] = useState('email');
  const openPlanHistory = (tab = 'email') => { setPlanHistoryTab(tab); setPlanHistoryOpen(true); };
  const [resendOffer, setResendOffer] = useState(null); // { pos, message }
  const [resendFlow, setResendFlow] = useState(null);   // pos being sent

  /*
   * Change quantity: the same change as editing the order (ChangeQuantityModal).
   * A PO the supplier or haulier already holds comes back as a revision to
   * send, through the same prompt as a moved date.
   */
  const [changeQtyRow, setChangeQtyRow] = useState(null);
  const onQuantityChanged = (result) => {
    const row = changeQtyRow;
    setChangeQtyRow(null);
    setLastAutoSaved(new Date());
    fetchOrders();
    fetchDatesWithChanges();
    const done = `${row?.order_number || 'Order'}: quantity changed.`;
    const pos = revisedPosFrom(result);
    if (pos.length) {
      setResendOffer({
        pos,
        message: `${done} ${pos.map(p => `${p.purchase_order_number} (${p.supplier_name || (p.po_type === 'haulage' ? 'haulier' : 'supplier')})`).join(', ')} `
          + `had already been sent with the old quantity. Send the revised ${pos.length === 1 ? 'version' : 'versions'}?`
      });
    } else {
      setAlertDialog({ open: true, title: 'Quantity Changed', message: done, type: 'success' });
    }
  };
  const prettyDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB') : '');
  const offerResend = (data, doneMessage) => {
    const list = Array.isArray(data?.resend) ? data.resend : [];
    if (!list.length) return false;
    const pos = list.map(p => ({
      ...p,
      revision: {
        summary: `new delivery date ${prettyDate(p.new_date)}`,
        changes: [`delivery date ${prettyDate(p.old_date) || 'none'} → ${prettyDate(p.new_date)}`]
      }
    }));
    setResendOffer({
      pos,
      message: `${doneMessage ? `${doneMessage} ` : ''}`
        + `${pos.map(p => `${p.purchase_order_number} (${p.supplier_name || (p.po_type === 'haulage' ? 'haulier' : 'supplier')})`).join(', ')} `
        + `had already been sent with the old date. Send the revised ${pos.length === 1 ? 'version' : 'versions'} with the new date?`
    });
    return true;
  };

  const applyMoveDate = async () => {
    const { orderIds, value } = moveDateModal;
    if (!value || !orderIds.length) return;
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/move-delivery-date`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerOrderIds: orderIds, newDate: value })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to move delivery date');
      setMoveDateModal({ open: false, value: '', orderIds: [], count: 0, optionDates: [] });
      setSelectedRows(new Set());
      setLastAutoSaved(new Date());
      fetchOrders();
      fetchDatesWithChanges();
      const dLabel = new Date(value + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      // The old day's haulier / driver / vehicle are cleared by the move (server).
      const cleared = data.transport_cleared?.message || '';
      const moved = `${data.movedOrders} order${data.movedOrders === 1 ? '' : 's'} moved to ${dLabel}.${cleared ? ` ${cleared}` : ''}`;
      if (!offerResend(data, moved)) {
        setAlertDialog({ open: true, title: 'Delivery Date Moved', message: moved, type: cleared ? 'warning' : 'success' });
      }
    } catch (e) {
      console.error('Failed to move delivery date:', e);
      setAlertDialog({ open: true, title: 'Error', message: `Failed to move delivery date: ${e.message}`, type: 'error' });
    }
  };

  // ── Option dates ────────────────────────────────────────────────────────
  const fmtOptDay = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10);
    return new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };
  const optionTooltip = (order) => {
    const list = Array.isArray(order.option_dates) ? order.option_dates : [];
    const parts = list.map(o => `${fmtOptDay(o.date)} (P${o.preference})`);
    const planned = order.delivery_date ? ` · Planned: ${fmtOptDay(order.delivery_date)}` : '';
    return `Option dates: ${parts.join(', ') || '—'}${planned}${order.is_selected_day ? '' : ' — not planned on this day'}`;
  };

  // "Plan on this day": commit the given orders to the currently-viewed day
  // (moves delivery_date to it, auto-deselecting the other option days).
  const planOnThisDay = async (rows) => {
    const orderIds = [...new Set(rows.map(o => o.id).filter(Boolean))];
    if (!orderIds.length || planFixed) return;
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/move-delivery-date`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerOrderIds: orderIds, newDate: selectedDate })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to plan on this day');
      setSelectedRows(new Set());
      setLastAutoSaved(new Date());
      fetchOrders();
      fetchDatesWithChanges();
      // Choosing another of its option days is a date move: the old day's
      // haulier / driver / vehicle are cleared (server) — said here.
      const cleared = data.transport_cleared?.message || '';
      if (!offerResend(data, `Planned on this day.${cleared ? ` ${cleared}` : ''}`) && cleared) {
        setAlertDialog({ open: true, title: 'Planned on this day', message: cleared, type: 'warning' });
      }
    } catch (e) {
      console.error('Failed to plan on this day:', e);
      setAlertDialog({ open: true, title: 'Error', message: `Failed to plan on this day: ${e.message}`, type: 'error' });
    }
  };

  // "Go to order" — in the right-click menu and the three-dots menu alike.
  // Opening the order only shows it, so it stays usable for view-only accounts.
  const goToOrderAction = (row) => ({
    key: 'go-to-order',
    readSafe: true,
    icon: 'shopping_cart',
    label: row.order_number ? `Go to order ${row.order_number}` : 'Go to order',
    disabled: !row.id,
    title: row.is_self_billed ? SELF_BILLED_NO_ORDER_REASON : undefined,
    onClick: () => setOrderModalId(row.id)
  });

  // Open the table context menu at the cursor for a row. Cmd/Ctrl+right-click is
  // left to the app's radial nav menu; a plain right-click opens this one.
  // Edit the customer order behind a row — same hand-off as Search Customer
  // Orders: stash the id and navigate to the Create/Edit Order screen.
  const handleEditOrder = (orderId) => {
    if (!onNavigate) return;
    sessionStorage.setItem('editOrderId', orderId);
    setOrderModalId(null);
    onNavigate('create-customer-order');
  };

  // Conditional alternating row colour keyed on driver changes: the first band
  // is coloured, it flips off at each driver change, back on at the next, etc.
  const driverBandIds = (() => {
    if (!driverColorsOn) return null;
    const set = new Set();
    let band = 0;
    orders.forEach((o, i) => {
      if (i > 0 && (orders[i].driver || '') !== (orders[i - 1].driver || '')) band++;
      if (band % 2 === 0) set.add(o.order_line_id);
    });
    return set;
  })();

  // ── Row partition + view filters ────────────────────────────────────────
  // Option-date CANDIDATE rows are shown here because this is one of the order's
  // option days, but the order is planned on a different day (not selected here).
  const isCandidateRow = isOptionCandidate;

  // Combined view filters (isolate driver + "planned-with-options only").
  const rowPassesFilters = (o) => {
    if (isolatedDriver !== null) {
      const drv = (o.driver || '').trim();
      const ok = isolatedDriver === '' ? drv === '' : (drv === isolatedDriver || drv === '');
      if (!ok) return false;
    }
    if (showOnlyOptionPlanned && !(o.is_selected_day && o.has_option_dates)) return false;
    return true;
  };

  // Planned rows get a 1..N number (candidates get none); drag needs the row's
  // index within the full `orders` array.
  const plannedNumberById = new Map();
  const indexOfId = new Map();
  let plannedCounter = 0;
  orders.forEach((o, i) => {
    indexOfId.set(o.order_line_id, i);
    if (!isCandidateRow(o)) { plannedCounter += 1; plannedNumberById.set(o.order_line_id, plannedCounter); }
  });
  const plannedCount = plannedCounter;

  const visiblePlanned = orders.filter(o => !isCandidateRow(o) && rowPassesFilters(o));
  const visibleCandidates = orders.filter(o => isCandidateRow(o) && rowPassesFilters(o));

  // Lorry day planning: the visible rows in their groups, in the BOARD's own
  // order (the saved sequence) — never re-sorted as times are typed (Cam,
  // 2026-10-15). A row that drops before one above it is marked "!" until
  // "Re-order by time" sorts the group first drop first (utils/lorryDayOrder).
  // Unassigned first; our lorries by haulier (ours first), then their first
  // collection; the groups someone else carries last.
  // (utils/boardGroups groupRows — the office summary's groups too.)
  const boardGroups = (() => {
    if (!lorryDayOn) return null;
    const groups = groupRows(visiblePlanned);
    for (const g of groups) g.outOfOrder = outOfOrderIds(g.rows);
    return groups;
  })();
  // ✓ ◐ ○ across the day's planned rows (option-date candidates excluded).
  const planCounts = (() => {
    const c = { planned: 0, part: 0, unplanned: 0 };
    if (!lorryDayOn) return c;
    for (const o of orders) {
      if (isCandidateRow(o)) continue;
      const st = statusOf(o).status;
      if (c[st] != null) c[st] += 1;
    }
    return c;
  })();

  // Folding a group away (per date, this tab): its header stays, rows hidden.
  const collapsedNow = new Set((collapsedGroups && collapsedGroups[selectedDate]) || []);
  const toggleGroup = (key) => setCollapsedGroups(prev => {
    const day = new Set((prev && prev[selectedDate]) || []);
    if (day.has(key)) day.delete(key); else day.add(key);
    // Keep the last fortnight of dates only.
    const kept = Object.keys(prev || {}).sort().slice(-14).filter(d => d !== selectedDate);
    return { ...Object.fromEntries(kept.map(d => [d, prev[d]])), [selectedDate]: [...day] };
  });
  const setAllCollapsed = (collapse) => setCollapsedGroups(prev => ({
    ...(prev || {}), [selectedDate]: collapse ? (boardGroups || []).map(g => g.key) : []
  }));

  // "Re-order by time": the group's rows first drop first, saved as the board
  // order (the same save as a drag in the board's ordinary mode).
  const reorderGroupByTime = (g) => {
    if (planFixed || !canEdit) return;
    const next = reorderGroup(orders, g.rows.map(r => r.order_line_id));
    if (next === orders) return;
    persistBoardOrder(next);
  };

  // A group's header row: who carries it and, for one of our lorries, its
  // day in short — efficiency, the most on board, a compartment strip at that
  // point, its checks — and "Open day". Rows dropped on it are assigned to it.
  const renderGroupHeader = (g) => {
    const day = g.lorryKey ? lorryDayByKey.get(g.lorryKey) : null;
    const counts = { planned: 0, part: 0, unplanned: 0 };
    g.rows.forEach(o => { const st = statusOf(o).status; if (counts[st] != null) counts[st] += 1; });
    const warns = day ? day.checks.filter(c => c.severity === 'warn') : [];
    const infos = day ? day.checks.filter(c => c.severity === 'info') : [];
    const cap = day?.body?.cap;
    const peakStop = day && day.peak.stopKey ? day.stops.find(x => x.key === day.peak.stopKey) : null;
    const pct = cap ? Math.min(100, (day.peak.tonnes / cap) * 100) : 0;
    const over = cap != null && day && day.peak.tonnes > cap + 0.0005;
    const overfill = day ? lorryOverfill(day) : null;
    const collapsed = collapsedNow.has(g.key);
    const reorderBlock = planFixed ? (noEditReason || (isPastDate ? 'This day has passed — its plan is kept as it was' : 'This day is locked'))
      : !canEdit ? noEditReason
      : (!g.outOfOrder || !g.outOfOrder.size) ? 'Already in time order — first drop at the top' : null;
    const reorderButton = (
      <div className="ld-reorder">
        <button type="button" className={`btn-slideout${reorderBlock ? ' btn-disabled' : ''}`} disabled={!!reorderBlock}
          onClick={() => reorderGroupByTime(g)}
          title={reorderBlock || `Sort ${g.kind === 'lorry' ? 'this lorry’s' : 'these'} rows by drop time — first drop at the top — and save the order`}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">sort</span></div>
          <span className="btn-label">Re-order by time</span>
        </button>
      </div>
    );
    return (
      <tr
        key={`group-${g.key}`}
        className={`ld-group ld-group-${g.kind}${dropTargetGroup === g.key ? ' ld-drop-target' : ''}${collapsed ? ' ld-group-collapsed' : ''}`}
        onDragOver={(e) => overGroup(e, g)}
        onDrop={(e) => dropOnGroup(e, g)}
        // The whole header row folds the lorry's rows (Cam, 16 Oct) — except a
        // click on one of its own controls or links, which does its own thing.
        onClick={(e) => {
          if (e.target.closest('button, a, input, select, textarea, label, [role="button"], .entity-link')) return;
          toggleGroup(g.key);
        }}
        title={collapsed ? `Click to show its ${g.rows.length} row${g.rows.length === 1 ? '' : 's'}` : 'Click to hide its rows (the summary stays)'}
      >
        <td colSpan={14}>
          <div className="ld-group-in">
            <button type="button" className="btn-icon ld-collapse" aria-expanded={!collapsed}
              aria-label={`${collapsed ? 'Show' : 'Hide'} the rows of ${g.kind === 'lorry' ? (g.vehicle || g.driver || g.haulier || 'this lorry') : g.label}`}
              title={collapsed ? `Show its ${g.rows.length} row${g.rows.length === 1 ? '' : 's'}` : 'Hide its rows (the summary stays)'}
              onClick={(e) => { e.stopPropagation(); toggleGroup(g.key); }}>
              <span className="material-symbols-rounded">{collapsed ? 'chevron_right' : 'expand_more'}</span>
            </button>
            {collapsed && g.kind !== 'unassigned' && g.kind !== 'other' && (
              <span className="ld-muted">{g.rows.length} row{g.rows.length === 1 ? '' : 's'} hidden</span>
            )}
            {g.kind === 'unassigned' && (
              <>
                <span className="ld-group-title">Unassigned</span>
                <span className="ld-muted">{g.rows.length} order{g.rows.length === 1 ? '' : 's'}</span>
                {canEdit && !planFixed && <span className="ld-hint">Drag a row onto a lorry to assign it</span>}
                {g.outOfOrder && g.outOfOrder.size > 0 && reorderButton}
              </>
            )}
            {g.kind === 'other' && (
              <>
                <span className="ld-group-title">{g.label}</span>
                <span className="ld-muted">{g.rows.length} order{g.rows.length === 1 ? '' : 's'} · not on our lorries</span>
              </>
            )}
            {g.kind === 'lorry' && (
              <>
                {g.vehicle
                  ? <span className="vl-reg"><EntityLink type="vehicle" id={g.vehicle === UNKNOWN_VEHICLE ? null : (vehicleSpecs[g.vehicle] || null)} variant="badge">{g.vehicle}</EntityLink></span>
                  : <span className="ld-chip ld-chip-warn">No vehicle</span>}
                {g.driver && (
                  <span className="vl-driver">
                    <span className="material-symbols-rounded">person</span>
                    <EntityLink type="driver" id={driverIdFor(g.driver)}>{g.driver}</EntityLink>
                  </span>
                )}
                {g.haulier && <span className="ld-muted">{g.haulier}</span>}
                {reorderButton}
                {g.lorryKey && (
                  <div className="ld-open">
                    <button type="button" className="btn-slideout" onClick={() => setLorryWindow(g.lorryKey)}
                      title="Open this lorry’s day — times, doors, compartments and checks">
                      <div className="icon-wrapper"><span className="material-symbols-rounded">route</span></div>
                      <span className="btn-label">Open day</span>
                    </button>
                  </div>
                )}
                {day && day.efficiency != null && cap != null && (
                  <span title={`Efficiency at the fullest point: ${day.efficiency}%`}>
                    <ProgressRing percentage={day.efficiency} color={SEVERITY_COLOR[efficiencySeverity({ efficiency: day.efficiency })]} size={34} />
                  </span>
                )}
                {day && cap != null && (
                  <span className="ld-peak" title="The most on board at once, against the usable payload">
                    <span className={`vl-bar ld-bar ${over ? 'vl-bar-over' : ''}`}><span className="vl-bar-fill" style={{ width: `${pct}%` }} /></span>
                    <strong className={over ? 'vl-over-text' : ''}>{formatQuantity(day.peak.tonnes)}</strong> / {formatTonnes(cap)}
                  </span>
                )}
                {day && day.isUnknown && cap == null && <span className="ld-chip ld-chip-warn" title="Set it in the lorry’s day">Payload not set</span>}
                {peakStop && (peakStop.compartments || []).length > 0 && (
                  <span className="ld-mini" title="The compartments at the fullest point">
                    {peakStop.compartments.map(c => <i key={c.n} style={{ flex: c.size || 1 }} className={c.items.length ? 'full' : ''} />)}
                  </span>
                )}
                {day && <span className="ld-muted">{day.collectCount} collection{day.collectCount === 1 ? '' : 's'} · {day.dropCount} drop{day.dropCount === 1 ? '' : 's'}</span>}
                <span className="ld-counts" title="Planned · part-planned · not planned">
                  <span className="ld-tick ld-tick-planned small"><span className="material-symbols-rounded">check</span></span>{counts.planned}
                  <span className="ld-tick ld-tick-part small" />{counts.part}
                </span>
                {overfill && overfill.any && (
                  <span className="ld-chip ld-chip-over" title={`${day.checks.filter(isOverfillCheck).map(c => `${c.stopLabel ? `${c.stopLabel}: ` : ''}${c.message}`).join('\n')}\nAllowed — check it is what you meant before sending.`}>
                    <span className="material-symbols-rounded">scale</span>
                    Over payload{overfill.layouts.length ? ` · compartments +${formatTonnes(Math.max(...overfill.layouts.map(l => l.by)))}` : ''}{overfill.onBoard ? ` · load +${formatTonnes(overfill.onBoard.by)}` : ''}
                  </span>
                )}
                {day && (warns.length
                  ? <span className="ld-chip ld-chip-warn" title={warns.map(c => `${c.stopLabel ? `${c.stopLabel}: ` : ''}${c.message}`).join('\n')}>{warns.length} to look at</span>
                  : infos.length
                    ? <span className="ld-chip ld-chip-info" title={infos.map(c => c.message).join('\n')}>{infos.length} for info</span>
                    : <span className="ld-chip ld-chip-ok">No checks</span>)}
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // One planning-table row. `candidate` rows (option days the order isn't planned
  // on) are greyed, numberless and non-draggable, but otherwise identical.
  const renderPlanningRow = (order, candidate, group = null) => {
    const index = indexOfId.get(order.order_line_id);
    const deliveryAddr = formatDeliveryAddress(order);
    const product = formatProduct(order);
    const poTooltip = order.order_number ? `SO: ${order.order_number}` : '';
    // Lorry day planning: rows are dragged onto a group to assign them.
    const assignDrag = lorryDayOn && !candidate && !planFixed && canEdit && !otherCarrierOf(order);
    const isDragging = !candidate && (lorryDayOn
      ? !!(assignDragRef.current && assignDragRef.current.includes(order.order_line_id))
      : ((dragBlockRef.current && dragBlockRef.current.includes(order.order_line_id)) || draggedIndex === index));
    const lp = lorryDayOn ? normaliseLinePlan(order.line_plan) : null;
    const plan = lorryDayOn ? planByRow.get(String(order.order_line_id)) : null;
    const dragProps = lorryDayOn
      ? {
        draggable: assignDrag,
        onDragStart: assignDrag ? (e) => startAssignDrag(e, order) : undefined,
        onDragOver: group ? (e) => overRow(e, group, order) : undefined,
        onDrop: group ? (e) => dropOnRow(e, group, order) : undefined,
        onDragEnd: assignDrag ? endAssignDrag : undefined
      }
      : {
        draggable: !planFixed && !candidate,
        onDragStart: (!planFixed && !candidate) ? (e) => handleDragStart(e, index) : undefined,
        onDragOver: (!planFixed && !candidate) ? (e) => handleDragOver(e, index) : undefined,
        onDragEnd: (!planFixed && !candidate) ? handleDragEnd : undefined
      };
    return (
      <tr
        key={order.order_line_id}
        {...dragProps}
        onContextMenu={ctxMenu.onRowContextMenu(order)}
        className={`${isDragging ? 'dragging' : ''} ${dropTargetRow && dropTargetRow.id === order.order_line_id ? (dropTargetRow.after ? 'ld-drop-after' : 'ld-drop-before') : ''} ${selectedRows.has(order.order_line_id) ? 'row-selected' : ''} ${driverBandIds && driverBandIds.has(order.order_line_id) ? 'driver-band' : ''} ${candidate ? 'option-day-row' : ''}`.trim()}
        style={{ color: getStatusColor(order.status), cursor: (planFixed || candidate || (lorryDayOn && !assignDrag)) ? 'default' : 'move' }}
      >
        <td className="col-select" onClick={(e) => e.stopPropagation()}>
          <label
            className="tick-check-label"
            style={{ justifyContent: 'center', gap: '0' }}
            onMouseDown={dragSelect.onMouseDown(order.order_line_id, selectedRows.has(order.order_line_id))}
            onMouseEnter={dragSelect.onMouseEnter(order.order_line_id)}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          >
            <input
              type="checkbox"
              className="tick-check-input"
              id={`select-tp-${order.order_line_id}`}
              checked={selectedRows.has(order.order_line_id)}
              onChange={() => toggleRowSelected(order)}
            />
            <label htmlFor={`select-tp-${order.order_line_id}`} className="tick-check">
              <svg width="16px" height="16px" viewBox="0 0 18 18">
                <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                <polyline points="1 9 7 14 15 4"></polyline>
              </svg>
            </label>
          </label>
        </td>
        <td className={`col-num ${candidate ? '' : 'drag-handle'}`} style={{ opacity: planFixed && !lorryDayOn ? 0.3 : 1 }}
          title={candidate ? undefined : (lorryDayOn ? (assignDrag ? 'Drag within this lorry to re-order it, or onto another lorry to assign it' : undefined) : 'Drag to reorder')}>
          {!candidate && !lorryDayOn && <span className="col-num-value">{plannedNumberById.get(order.order_line_id)}</span>}
          {!candidate && lorryDayOn && (() => {
            const st = statusOf(order);
            return (
              <span className={`ld-tick ld-tick-${st.status}`} title={statusTitle(st)} aria-label={statusTitle(st)}>
                {st.status === 'planned' ? <span className="material-symbols-rounded">check</span> : null}
              </span>
            );
          })()}
          {!candidate && lorryDayOn && group && group.outOfOrder && group.outOfOrder.has(order.order_line_id) && (
            <span className="ld-out-of-order" role="img"
              aria-label="Out of time order"
              title="Out of time order — this row drops before one above it. Rows are not moved while times are typed: use “Re-order by time” on the group’s header when you are done.">!</span>
          )}
          {order.has_option_dates && (
            <span
              className={`opt-badge ${order.is_selected_day ? 'opt-planned' : 'opt-candidate'}`}
              title={optionTooltip(order)}
            >
              {order.is_selected_day ? 'OPT' : `P${order.option_preference ?? '?'}`}
            </span>
          )}
        </td>
        <td className="col-supplier">
          <div className="supplier-info">
            {order.supplier_name ? (
              <span
                className="supplier-abbr"
                title={`${order.supplier_name}${order.supplier_account_no ? ` (${order.supplier_account_no})` : ''}`}
              >
                <EntityLink type="contact" id={order.supplier_account_id} draggable={false}>
                  {abbreviateSupplier(order.supplier_name)}
                </EntityLink>
              </span>
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </td>
        <td className="col-customer">
          <div className="customer-info">
            <div className="customer-name-row">
              {order.customer_account_no && (
                <span className="account-badge">
                  <EntityLink type="contact" id={order.customer_account_id || order.delivery_contact_id} variant="badge" draggable={false}>
                    {order.customer_account_no}
                  </EntityLink>
                </span>
              )}
              <span className="customer-name">
                {/* A self-billed row's "customer" is where its goods go (no account of ours). */}
                <EntityLink type="contact" id={order.customer_account_id || order.delivery_contact_id} draggable={false}>
                  {order.customer_name}
                </EntityLink>
              </span>
            </div>
            {deliveryAddr && (() => {
              const parts = deliveryAddr.split('\n').filter(Boolean);
              const postcode = parts.length > 1 ? parts[parts.length - 1] : '';
              const firstLine = parts[0] || '';
              const hasMore = parts.length > 2 || (parts.length === 2 && postcode !== parts[1]);
              const isExpanded = expandedAddresses.has(order.order_line_id);
              const summary = postcode && postcode !== firstLine ? `${firstLine}, ${postcode}` : firstLine;
              return (
                <div className={`address-line ${isExpanded ? 'address-expanded' : ''}`}>
                  <span className="address-summary">{summary}</span>
                  {hasMore && (
                    <span
                      className="address-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedAddresses(prev => {
                          const next = new Set(prev);
                          next.has(order.order_line_id) ? next.delete(order.order_line_id) : next.add(order.order_line_id);
                          return next;
                        });
                      }}
                    >
                      <span className="material-symbols-rounded">{isExpanded ? 'remove' : 'add'}</span>
                    </span>
                  )}
                  {hasMore && (
                    <div className="address-full">
                      {parts.slice(1).map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* The planners' own note: on the board only, never printed or sent. */}
            {lp && lp.note && (
              <div className="planner-note-line" role="button" tabIndex={0}
                title="Planner’s note — for the planners only, never printed or sent. Click to change it."
                onClick={(e) => { e.stopPropagation(); setNoteFor(order); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setNoteFor(order); } }}>
                <span className="material-symbols-rounded">sticky_note_2</span>
                <span>{lp.note}</span>
              </div>
            )}
          </div>
        </td>
        <td className="col-instructions">
          <span
            className={`notes-info-icon ${orderHasNotes(order) ? 'has-notes' : ''}`}
            title="Customer/ Order Notes"
            onClick={(e) => { e.stopPropagation(); openNotes(order); }}
          >
            <span className="material-symbols-rounded">info</span>
          </span>
          {order.delivery_instructions || <span className="text-muted">—</span>}
          {/* What will physically get down the lane. No label: the lorry icon
              says what it is, and on a planning board every character of width
              is spent on something. */}
          {order.customer_largest_vehicle_access?.trim() && (
            <div className="lva-line" title="Largest vehicle access">
              <span className="material-symbols-rounded">local_shipping</span>
              <span>{order.customer_largest_vehicle_access.trim()}</span>
            </div>
          )}
        </td>
        <td className="col-product">
          <div className="products-info">
            {product ? (
              <div className="product-line">
                {product.product_code && (
                  <span className="product-code">
                    <EntityLink type="inventory_item" id={order.inventory_item_id} variant="badge" draggable={false}>
                      {product.product_code}
                    </EntityLink>
                  </span>
                )}
                <span className="product-desc">{product.description}</span>
              </div>
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </td>
        <td className="col-qty">
          {formatQuantity(parseFloat(order.quantity || 0)) || '0.00'}
          {order.line_tonnes != null && (
            <div className="qty-tonnes" title="Estimated delivery weight (from inventory unit weight)">
              {formatTonnes(Number(order.line_tonnes))}
            </div>
          )}
        </td>
        <td className="col-po order-number" title={poTooltip}>
          {/* Stock rows have no PO number — show their sales order number. */}
          <div className="order-number-primary"><RowReferenceLink order={order} /></div>
          <div className="order-number-secondary">{order.supplier_reference || '—'}</div>
        </td>
        <td className='col-status status-col'>
          <div className="status-row">
            {order.has_changes ? (
              <span
                className="modified-icon"
                title="Order modified - click to view changes"
                onClick={(e) => { e.stopPropagation(); handleShowChanges(order, true); }}
              >
                <span className="material-symbols-rounded">warning</span>
              </span>
            ) : order.has_history ? (
              <span
                className="history-icon"
                title="View change history"
                onClick={(e) => { e.stopPropagation(); handleShowChanges(order, false); }}
              >
                <span className="material-symbols-rounded">history</span>
              </span>
            ) : <span className="history-icon" style={{ visibility: 'hidden' }}><span className="material-symbols-rounded">history</span></span>}
            {(() => {
              // Stock rows have no PO, so no PO status — badge them as coming off
              // stock (as the Haulage/Bag board does). Calling .toLowerCase() on
              // the null status is what took the whole board down.
              const badge = order.is_stock_line ? 'Stock' : order.status;
              return badge ? (
                <span className={`status-badge status-${String(badge).toLowerCase().replace(/ /g, '-')}`}>
                  {badge}
                </span>
              ) : null;
            })()}
            {order.is_customer_collection && (
              <span className="status-badge status-customer-collection" title="The customer collects this order — no haulier, driver or vehicle of ours">
                Customer Collection
              </span>
            )}
            {order.third_party_board && (
              <span className="status-badge status-third-party" title={thirdPartyBadgeTitle(order)}>
                Third-party
              </span>
            )}
            {order.is_self_billed && (
              <span className={`status-badge status-self-billed${selfBilledRowPlanned(order) ? '' : ' unplanned'}`}
                title={selfBilledRowPlanned(order) ? SELF_BILLED_BADGE_TITLE : `${SELF_BILLED_BADGE_TITLE} Not planned yet — it will not be on the driver paperwork.`}>
                {SELF_BILLED_LABEL}
              </span>
            )}
            {order.is_redelivery && (
              <span className={`status-badge status-redelivery${redeliveryRowPlanned(order) ? '' : ' unplanned'}`}
                title={`${redeliveryTitle(order.redelivery)}\nCollected from where it is held; never sent to a mill.`}>
                {REDELIVERY_LABEL}
              </span>
            )}
            <span
              className={`price-icon ${order.priced ? 'color-success' : 'color-error'}`}
              title={order.priced ? 'Priced' : 'Not priced'}
            >
              <span className="material-symbols-rounded">verified</span>
            </span>
          </div>
        </td>
        <td className="col-time">
          <div className="time-cell">
            <PlanTimeInput
              className="time-input"
              navRow={order.order_line_id}
              navCol={0}
              value={order.scheduled_time}
              disabled={planFixed}
              title={planFixed && controlBlock ? controlBlock : (lorryDayOn ? 'Collection time' : undefined)}
              ariaLabel={`Collection time, ${order.customer_name || ''}`}
              onCommit={(t) => commitCollectionTime(order, t)}
            />
            {lorryDayOn ? (() => {
              // Lorry day planning: collect → drop, the compartments, a note.
              const carrier = otherCarrierOf(order);
              const dropBlocked = candidate ? 'Planned on another of its option dates'
                : carrier ? `${carrier} — no drop of ours to time`
                : order.no_line_plan ? 'Self-billed PO — its drop time is kept once a database update has been applied'
                : lorryDayReadOnly;
              const compTitle = plan && plan.compartmentLabel
                ? `Compartment${plan.compartments.length > 1 ? 's' : ''} ${plan.compartments.join(', ')} — 1 is nearest the cab. Change in the lorry’s day.`
                : (inLorryDay(order) ? 'No compartment yet — choose it in the lorry’s day' : 'Not on a lorry yet');
              return (
                <>
                  <span className="ld-arrow" aria-hidden="true">→</span>
                  <PlanTimeInput
                    className="time-input ld-drop-input"
                    navRow={order.order_line_id}
                    navCol={1}
                    value={lp.dropTime}
                    disabled={!!dropBlocked || candidate}
                    title={dropBlocked || 'Drop time'}
                    ariaLabel={`Drop time, ${order.customer_name || ''}`}
                    onCommit={(t) => saveDropTime(order, t)}
                  />
                  <span className={`ld-comp-badge${plan && plan.compartmentLabel ? '' : ' empty'}`} title={compTitle}>
                    {plan && plan.compartmentLabel ? plan.compartmentLabel : ''}
                  </span>
                  <button type="button" className={`btn-icon ld-note-btn${lp.note ? ' has-note' : ''}${order.no_line_plan ? ' btn-disabled' : ''}`}
                    title={order.no_line_plan ? 'Self-billed PO — its note is kept once a database update has been applied'
                      : (lp.note ? `Planner’s note: ${lp.note}` : 'Add a planner’s note — for the planners only, never printed or sent')}
                    aria-label="Planner’s note"
                    disabled={!!order.no_line_plan}
                    onClick={(e) => { e.stopPropagation(); setNoteFor(order); }}>
                    <span className="material-symbols-rounded">{lp.note ? 'sticky_note_2' : 'note_add'}</span>
                  </button>
                </>
              );
            })() : (
              /* Drop number within its load — D1 is the first drop. Blank until
                 the row is on a vehicle the board can load-check. */
              <span
                className={`drop-badge${order.run_key ? ' in-run' : ''}`}
                title={dropNumbers.has(order.order_line_id)
                  ? `Drop ${dropNumbers.get(order.order_line_id)} of its load${order.run_key ? ' (combined run)' : ''}`
                  : 'No drop number until the row is on a vehicle'}
              >
                {dropNumbers.has(order.order_line_id) ? `D${dropNumbers.get(order.order_line_id)}` : ''}
              </span>
            )}
          </div>
        </td>
        <td className="col-haulier">
          <SearchableDropdown
            options={[
              { value: '', label: 'None' },
              ...hauliers.map(h => ({ value: h, label: h })),
              // The row's own haulier even when it is not in the list — a rep
              // cannot load the list (edit permission), and an archived haulier
              // is not in it; either way the cell showed blank. (Driver already
              // did this.)
              ...(order.haulier && !hauliers.includes(order.haulier) ? [{ value: order.haulier, label: order.haulier }] : [])
            ]}
            value={order.haulier || ''}
            onChange={(value) => updateHaulier(order.order_line_id, value)}
            placeholder="Select..."
            searchPlaceholder="Search..."
            disabled={cellsFixed || order.is_customer_collection}
          />
        </td>
        <td className="col-driver">
          {(() => {
            const filtered = order.haulier
              ? drivers.filter(d => (d.hauliers || []).some(h => h.name === order.haulier))
              : drivers;
            const names = new Set(filtered.map(d => d.name));
            if (order.driver && !names.has(order.driver)) names.add(order.driver);
            const opts = [
              { value: '', label: 'None' },
              ...Array.from(names).sort().map(n => ({ value: n, label: n }))
            ];
            return (
              <SearchableDropdown
                options={opts}
                value={order.driver || ''}
                onChange={(value) => updateDriver(order.order_line_id, value)}
                placeholder="Select..."
                searchPlaceholder="Search..."
                disabled={cellsFixed || order.is_customer_collection}
              />
            );
          })()}
        </td>
        <td className="col-vehicle">
          <Dropdown
            options={[
              { value: '', label: 'None' },
              { value: UNKNOWN_VEHICLE, label: 'Unknown (set payload)' },
              ...vehicles.map(v => ({ value: v, label: v })),
              // As for haulier: always offer the row's own vehicle.
              ...(order.vehicle && order.vehicle !== UNKNOWN_VEHICLE && !vehicles.includes(order.vehicle) ? [{ value: order.vehicle, label: order.vehicle }] : [])
            ]}
            value={order.vehicle || ''}
            onChange={(value) => updateVehicle(order.order_line_id, value)}
            placeholder="Select..."
            searchPlaceholder="Search..."
            disabled={cellsFixed || order.is_customer_collection}
          />
        </td>
        {/* The row's three-dots menu: the same row actions as right-click
            for go-to-order and archiving. Not draggable from here. */}
        <td className="col-actions" onClick={(e) => e.stopPropagation()}>
          <div className="actions-cell">
            <RowActionsMenu actions={[goToOrderAction(order), ...rowRedeliver.actionsFor(order), ...rowArchive.actionsFor(order)]} />
          </div>
        </td>
      </tr>
    );
  };

  // ── Drag auto-scroll (enlarged trigger zone, eased + gentle speed) ──────
  const runAutoScroll = () => {
    const el = transportScrollRef.current;
    const v = autoScrollRef.current.velocity;
    if (!el || v === 0) { autoScrollRef.current.raf = null; return; }
    el.scrollTop += v;
    autoScrollRef.current.raf = requestAnimationFrame(runAutoScroll);
  };

  const updateAutoScroll = (clientY) => {
    const el = transportScrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ZONE = 130;      // px — enlarged trigger band top & bottom
    const MAX_SPEED = 6;   // px/frame (~360px/s at 60fps) — gentle + smooth
    let velocity = 0;
    if (clientY < rect.top + ZONE) {
      const intensity = Math.min(1, (rect.top + ZONE - clientY) / ZONE);
      velocity = -MAX_SPEED * intensity;
    } else if (clientY > rect.bottom - ZONE) {
      const intensity = Math.min(1, (clientY - (rect.bottom - ZONE)) / ZONE);
      velocity = MAX_SPEED * intensity;
    }
    autoScrollRef.current.velocity = velocity;
    if (velocity !== 0 && !autoScrollRef.current.raf) {
      autoScrollRef.current.raf = requestAnimationFrame(runAutoScroll);
    }
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current.raf) cancelAnimationFrame(autoScrollRef.current.raf);
    autoScrollRef.current.raf = null;
    autoScrollRef.current.velocity = 0;
  };

  // ── Drag & drop (single row, or a multi-selected block moved together) ──
  const handleDragStart = (e, index) => {
    // A drag starting on the checkbox cell is a select-paint, not a row move.
    if (e.target.closest && e.target.closest('.col-select')) {
      e.preventDefault();
      return;
    }
    const row = orders[index];
    // If the dragged row is part of a multi-selection, drag the whole block.
    dragBlockRef.current = (selectedRows.has(row.order_line_id) && selectedRows.size > 1)
      ? orders.filter(o => selectedRows.has(o.order_line_id)).map(o => o.order_line_id)
      : [row.order_line_id];
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    updateAutoScroll(e.clientY);

    const block = dragBlockRef.current;
    if (!block || block.length === 0) return;
    const blockSet = new Set(block);
    const hoveredRow = orders[index];
    // Hovering over the block itself → nothing to do (prevents jitter).
    if (!hoveredRow || blockSet.has(hoveredRow.order_line_id)) return;

    const blockRows = orders.filter(o => blockSet.has(o.order_line_id));
    const rest = orders.filter(o => !blockSet.has(o.order_line_id));
    const rIdx = rest.findIndex(o => o.order_line_id === hoveredRow.order_line_id);
    if (rIdx < 0) return;
    // Drop the block after the hovered row when dragging down, before it when
    // dragging up — so its first row lands on the hovered slot.
    const firstBlockIdx = orders.findIndex(o => o.order_line_id === block[0]);
    const insertAt = firstBlockIdx < index ? rIdx + 1 : rIdx;
    const newOrders = [...rest.slice(0, insertAt), ...blockRows, ...rest.slice(insertAt)];

    if (newOrders.some((o, i) => o.order_line_id !== orders[i].order_line_id)) {
      setOrders(newOrders);
      setDraggedIndex(newOrders.findIndex(o => o.order_line_id === block[0]));
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    dragBlockRef.current = null;
    stopAutoScroll();
    // Use ref to get latest orders (avoids stale closure from async state updates)
    updateOrderSequence(ordersRef.current);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${dayNames[date.getDay()]}\n${date.getDate()}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PO Confirmed':
        return 'var(--text-primary)';
      case 'PO Created':
        return 'var(--color-primary)';
      case 'PO Sent':
        return 'var(--color-error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const formatDeliveryAddress = (order) => {
    // Countries/regions to exclude
    const excludeCountries = ['united kingdom', 'gb', 'gbr', 'uk', 'england', 'wales', 'scotland', 'great britain'];
    const customerNameLower = (order.customer_name || '').toLowerCase().trim();

    // Helper to filter address parts
    const filterPart = (p) => {
      if (!p || (typeof p === 'string' && !p.trim())) return false;
      if (typeof p !== 'string') return false;
      const pLower = p.toLowerCase().trim();
      // Skip if it matches customer name
      if (customerNameLower && pLower === customerNameLower) return false;
      // Skip country values
      if (excludeCountries.includes(pLower)) return false;
      return true;
    };

    // Helper to normalize address object keys (handles both camelCase and snake_case)
    const normalizeAddress = (addr) => {
      if (!addr || typeof addr !== 'object') return null;
      return {
        attentionTo: addr.attentionTo || addr.attention_to || addr.AttentionTo || '',
        addressLine1: addr.addressLine1 || addr.address_line_1 || addr.AddressLine1 || addr.line1 || '',
        addressLine2: addr.addressLine2 || addr.address_line_2 || addr.AddressLine2 || addr.line2 || '',
        addressLine3: addr.addressLine3 || addr.address_line_3 || addr.AddressLine3 || addr.line3 || '',
        addressLine4: addr.addressLine4 || addr.address_line_4 || addr.AddressLine4 || addr.line4 || '',
        city: addr.city || addr.City || addr.town || addr.Town || '',
        region: addr.region || addr.Region || addr.county || addr.County || addr.state || addr.State || '',
        postalCode: addr.postalCode || addr.postal_code || addr.PostalCode || addr.postcode || addr.Postcode || addr.zipCode || addr.zip || ''
      };
    };

    // Helper to build full address from address object
    const buildFullAddress = (addr, suffix = '') => {
      const normalized = normalizeAddress(addr);
      if (!normalized) return '';

      const parts = [
        normalized.attentionTo,
        normalized.addressLine1,
        normalized.addressLine2,
        normalized.addressLine3,
        normalized.addressLine4,
        normalized.city,
        normalized.region,
        normalized.postalCode
      ].filter(filterPart);

      if (parts.length === 0) return '';
      const fullAddr = parts.join('\n');
      return suffix ? `${fullAddr} ${suffix}` : fullAddr;
    };

    // Helper to safely parse JSON, returning null on failure
    const safeParseJson = (str) => {
      if (!str) return null;
      if (typeof str !== 'string') return str; // Already an object
      try {
        const parsed = JSON.parse(str);
        // Handle double-encoded JSON strings
        if (typeof parsed === 'string') {
          try {
            return JSON.parse(parsed);
          } catch (e) {
            // It was a JSON string containing a plain string address
            return parsed;
          }
        }
        return parsed;
      } catch (e) {
        return null; // Not valid JSON
      }
    };

    // Helper to check if something looks like an address object
    const isAddressObject = (obj) => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
      // Check for common address keys
      const addressKeys = ['addressLine1', 'address_line_1', 'AddressLine1', 'line1', 'city', 'City', 'postcode', 'postalCode', 'postal_code'];
      return addressKeys.some(key => obj[key]);
    };

    // Helper to clean a string address (handles newline or comma-separated addresses)
    const cleanStringAddress = (str, suffix = '') => {
      if (!str || typeof str !== 'string') return '';
      // Split by newlines first, then by commas, and filter parts (removes customer name, country, etc.)
      const parts = str
        .split(/[\n\r]+/)
        .flatMap(line => line.split(','))
        .map(p => p.trim())
        .filter(filterPart);
      if (parts.length === 0) return '';
      const result = parts.join('\n');
      return suffix ? `${result} ${suffix}` : result;
    };

    // First try PO delivery address
    if (order.delivery_address) {
      const parsed = safeParseJson(order.delivery_address);

      if (parsed && isAddressObject(parsed)) {
        const result = buildFullAddress(parsed);
        if (result) return result;
      } else if (typeof parsed === 'string' && parsed.trim()) {
        // It was a JSON-encoded plain string address (often newline-separated from auto-generated POs)
        const cleaned = cleanStringAddress(parsed);
        if (cleaned) return cleaned;
      } else if (typeof order.delivery_address === 'string' && order.delivery_address.trim()) {
        // Plain string that wasn't valid JSON - clean and use it
        const cleaned = cleanStringAddress(order.delivery_address);
        if (cleaned) return cleaned;
      }
    }

    // Try to get delivery address from flat customer columns
    if (order.customer_delivery_line1) {
      const addrObj = {
        addressLine1: order.customer_delivery_line1,
        addressLine2: order.customer_delivery_line2,
        addressLine3: order.customer_delivery_line3,
        addressLine4: order.customer_delivery_line4,
        city: order.customer_delivery_city,
        region: order.customer_delivery_region,
        postalCode: order.customer_delivery_postcode,
        country: order.customer_delivery_country
      };
      const result = buildFullAddress(addrObj);
      if (result) return result;
    }

    // Fallback to customer invoice address (from address column)
    if (order.customer_invoice_address) {
      const parsed = safeParseJson(order.customer_invoice_address);

      if (parsed && isAddressObject(parsed)) {
        const result = buildFullAddress(parsed, '(INV)');
        if (result) return result;
      } else if (typeof parsed === 'string' && parsed.trim()) {
        const cleaned = cleanStringAddress(parsed, '(INV)');
        if (cleaned) return cleaned;
      } else if (typeof order.customer_invoice_address === 'string' && order.customer_invoice_address.trim()) {
        const cleaned = cleanStringAddress(order.customer_invoice_address, '(INV)');
        if (cleaned) return cleaned;
      }
    }

    return '';
  };

  const formatProduct = (order) => {
    // Each row now represents a single order line
    if (!order.product_code && !order.product_name) {
      return null;
    }
    return {
      product_code: order.product_code,
      description: order.product_name
    };
  };

  // Day-level rollup shown above the vehicle load cards.
  const daySummary = useMemo(() => {
    // Customer Collection orders stay on the board but are no delivery of
    // ours, so they are left out of every count here (Cam, 2026-10-02).
    // So is a self-billed PO someone else carries (utils/selfBilledPo).
    const orders = ourDeliveryRows(boardOrders);
    const distinct = (vals) => new Set(vals.filter(v => v && String(v).trim())).size;
    const totalTonnes = orders.reduce((sum, o) => {
      const t = Number(o.line_tonnes);
      return sum + (Number.isFinite(t) ? t : (Number(o.quantity) || 0));
    }, 0);
    // Distinct delivery destinations — key on the formatted address, falling
    // back to the customer name when no address is present.
    const addressKeys = orders.map(o => {
      const a = formatDeliveryAddress(o);
      return (a ? a.replace(/\s+/g, ' ').trim().toLowerCase() : (o.customer_name || '').toLowerCase());
    }).filter(Boolean);
    // "Our" = the Excel Agri haulier; everything else is 3rd-party.
    const isExcel = (o) => (o.haulier || '').trim().toLowerCase() === 'excel agri';
    return {
      orders: orders.length,
      totalTonnes,
      addresses: new Set(addressKeys).size,
      suppliers: distinct(orders.map(o => o.supplier_name || o.contact_name)),
      ourVehicles: distinct(orders.filter(isExcel).map(o => o.vehicle)),
      thirdPartyDrivers: distinct(orders.filter(o => !isExcel(o)).map(o => o.driver))
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardOrders]);

  const formatAutoSaveTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) return 'Just now';
    if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Validate and prepare driver paperwork
  const handleDriverPaperwork = () => {
    const warnings = [];

    // Customer Collection: no haulier or time of ours to check. An unplanned
    // self-billed PO is not missing anything — it never needs planning, and
    // is left out of the paperwork (the send window says so).
    driverPaperworkRows(ourDeliveryRows(orders)).forEach(order => {
      const issues = [];
      if (!order.haulier) issues.push('no haulier');
      if (!order.scheduled_time) issues.push('no time');
      if (!order.quantity || order.quantity === 0) issues.push('0 quantity');
      if (!order.delivery_address && !order.customer_name) issues.push('no delivery address');
      if (!order.supplier_name && !order.contact_name) issues.push('no supplier');

      if (issues.length > 0) {
        warnings.push({
          po_number: rowReference(order),
          // Carried so the warning rows can link the same way the board does.
          order,
          customer_account_no: order.customer_account_no,
          customer_account_id: order.customer_account_id,
          customer: order.customer_name || 'Unknown',
          product_code: order.product_code,
          inventory_item_id: order.inventory_item_id,
          product: order.product_name || 'Unknown',
          quantity: order.quantity,
          issues
        });
      }
    });

    setPaperworkModal({ open: true, warnings, generating: false, progress: 0 });
  };

  // Unlock with the shared transport unlock password — checked by the server
  // (PUT /lock), which also re-locks a past day after the correction window.
  const handleUnlock = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/lock/${selectedDate}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isLocked: false, password: unlockModal.password })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setIsLocked(data.isLocked);
        setUnlockedUntil(data.unlockedUntil || null);
        setUnlockModal({ open: false, password: '', error: null });
      } else {
        setUnlockModal(prev => ({ ...prev, error: data.error || 'Could not unlock the day' }));
      }
    } catch (error) {
      console.error('Unlock failed:', error);
      setUnlockModal(prev => ({ ...prev, error: 'Could not reach the server to unlock the day' }));
    }
  };

  // A past day re-locks itself when its correction window ends; follow it.
  useEffect(() => {
    if (!unlockedUntil) return undefined;
    const ms = new Date(unlockedUntil).getTime() - Date.now();
    const timer = setTimeout(() => fetchLockStatus(selectedDate, isPastDate), Math.max(ms, 0) + 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockedUntil, selectedDate]);

  // Open driver selection modal after paperwork validation.
  // Paperwork is grouped by DRIVER — each driver gets one email with only their
  // orders. Rows with no driver assigned fall back to a haulier (or "Unassigned")
  // group so they still get sent. Suppliers/mills are never a grouping boundary.
  const openHaulierSelection = () => {
    setPaperworkModal({ open: false, warnings: [], generating: false, progress: 0 });

    // One row per driver (else haulier / Unassigned); the address is the
    // driver's own, else their haulier's — a fallback the window says out
    // loud (utils/driverPaperworkRecipients, shared with the other board).
    // Customer Collection orders are in no driver's paperwork, nor is an
    // unplanned self-billed PO (utils/selfBilledPo).
    const list = buildDriverPaperworkRecipients(driverPaperworkRows(ourDeliveryRows(orders)), drivers, hauliersWithEmail);

    // "Detailed day sheet" starts off for every driver, every send (Cam).
    setHaulierSelectionModal({
      open: true, hauliers: list.map(h => ({ ...h, detailed: false })), sendToOffice: false, copyToOffice: false,
      layoutChoice: { loading: true, available: false, reason: null }, layoutDate: selectedDate
    });
    // Whether this day has a detailed sheet at all — the server's rule
    // (lorryDayService.dayRules), so a day planned before lorry day planning
    // says so rather than offering a sheet it cannot print.
    const date = selectedDate;
    fetch(`${API_URL}/api/transport/planning/paperwork-layout/${date}`, { credentials: 'include' })
      .then(r => r.json().catch(() => ({})))
      .then(data => ({ loading: false, available: data?.available === true, reason: data?.reason || (data?.success ? null : 'Could not check how this day was planned') }))
      .catch(() => ({ loading: false, available: false, reason: 'Could not check how this day was planned' }))
      .then(layoutChoice => setHaulierSelectionModal(prev => (prev.open && prev.layoutDate === date ? { ...prev, layoutChoice } : prev)));
  };

  const toggleDetailed = (key, detailed) => setHaulierSelectionModal(prev => ({
    ...prev, hauliers: prev.hauliers.map(h => h.key === key ? { ...h, detailed } : h)
  }));

  const toggleHaulier = (key, include) => setHaulierSelectionModal(prev => ({
    ...prev, hauliers: prev.hauliers.map(h => h.key === key ? { ...h, include } : h)
  }));

  // "Add <name>'s email" in the send window: saved to the driver (checked
  // and audited by the server), so this pack — and every one after it —
  // goes to them directly instead of to their haulier's address.
  const saveRecipientEmail = async (key, email) => {
    const row = haulierSelectionModal.hauliers.find(h => h.key === key);
    if (!row?.driverId) throw new Error('This driver is not in Transport Settings → Drivers');
    const saved = await saveDriverEmail(row.driverId, email);
    setDrivers(prev => prev.map(d => (d.id === saved?.id ? { ...d, ...saved } : d)));
    setHaulierSelectionModal(prev => ({ ...prev, hauliers: withDriverEmail(prev.hauliers, key, saved?.email || email) }));
  };


  // Generate and send driver paperwork
  // `notice` comes from PaperworkSendModal: shown under the greeting of each email.
  const generatePaperwork = async ({ notice = '', sendAs = 'admin' } = {}) => {
    const { sendToOffice, copyToOffice, layoutChoice } = haulierSelectionModal;
    const layoutOpen = layoutChoice?.available === true;
    const selectedHauliers = haulierSelectionModal.hauliers.filter(h => h.include && (sendToOffice || h.email));

    if (selectedHauliers.length === 0) {
      setAlertDialog({
        open: true,
        title: 'No Hauliers Selected',
        message: 'Please select at least one haulier to send paperwork to.',
        type: 'warning'
      });
      return;
    }

    // Check all selected hauliers have email addresses
    setHaulierSelectionModal(prev => ({ ...prev, generating: true }));

    try {
      // Build driver-group selections for the API (keyed by the group key).
      const driverSelections = selectedHauliers.map(h => ({
        key: h.key,
        email: h.email,
        include: true,
        // The detailed lorry day sheet for this driver (off unless switched on).
        ...(layoutOpen && h.detailed ? { detailed: true } : {})
      }));

      const response = await fetch(`${API_URL}/api/transport/planning/generate-paperwork`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: selectedDate,
          orders: driverPaperworkRows(ourDeliveryRows(orders)),
          driverSelections,
          sendToOffice,
          // Cc the office on each driver's own email (never instead of them).
          copyToOffice: !sendToOffice && !!copyToOffice,
          notice,
          sendAs
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate paperwork');
      }

      const result = await response.json();

      // Close modal
      setHaulierSelectionModal({ open: false, hauliers: [] });

      // Refresh paperwork log
      fetchPaperworkLog(selectedDate);

      // Show success message
      const successCount = result.successCount || result.haulierCount;
      const failedCount = result.failedCount || 0;

      if (failedCount > 0) {
        const failedHauliers = result.emailResults
          ?.filter(r => !r.success)
          .map(r => `${r.haulier}: ${r.error}`)
          .join('\n') || '';

        setAlertDialog({
          open: true,
          title: 'Driver Paperwork Partially Sent',
          message: `Sent to ${successCount} driver(s), ${failedCount} failed.\n\nFailed:\n${failedHauliers}`,
          type: 'warning'
        });
      } else {
        setAlertDialog({
          open: true,
          title: 'Driver Paperwork Sent',
          message: `Driver Paperwork sent successfully to ${successCount} driver(s).`,
          type: 'success'
        });
      }

    } catch (error) {
      console.error('Failed to generate driver paperwork:', error);
      setHaulierSelectionModal(prev => ({ ...prev, generating: false }));
      setAlertDialog({
        open: true,
        title: 'Error',
        message: `Failed to generate driver paperwork: ${error.message}`,
        type: 'error'
      });
    }
  };

  // ── Office summary (full-colour day summary PDF emailed to the office) ──
  const openOfficeSummary = () => {
    setOfficeSummaryModal({ open: true, generating: false });
  };

  // Build the pre-formatted payload the server renders into the summary PDF.
  // The rows are grouped as the board groups them (utils/boardGroups): not
  // yet assigned first, then our lorries by first collection, then outside
  // hauliers'; each lorry's rows in the order it does them — drop order on a
  // day under the lorry day rules, else by collection time (a run at its first
  // time) — with each row's collection and drop times and its D#. `groups`
  // carries them under their headings; `deliveries` is the same rows flat, in
  // the same order (the counts, and a server from before groups).
  const buildOfficeSummaryPayload = () => {
    // The office summary counts our deliveries only: Customer Collection
    // orders are left out of it, as they are of Today's Deliveries.
    const orders = ourDeliveryRows(boardOrders);
    const RING_CIRC = 125.66; // 2πr for r=20, matches transport-summary.css
    const fitLabelFor = (fit) => (
      fit === 'ok' ? 'Fits'
        : fit === 'too-many' ? 'Too many drops'
        : fit === 'wont-fit' ? "Won't fit"
        : fit === 'reorder' ? 'Re-sequence'
        : 'No compartment spec'
    );

    const toDelivery = (o) => {
      const addr = formatDeliveryAddress(o);
      return {
        supplierAccount: o.supplier_account_no || '',
        supplierName: o.supplier_name || o.contact_name || '',
        customerAccount: o.customer_account_no || '',
        customerName: o.customer_name || '',
        deliveryAddress: addr ? addr.split('\n').filter(Boolean).join(', ') : '',
        // The office summary's Del. Inst. cell carries everything a driver or
        // the office needs to know: the delivery instructions (the order's,
        // with the PO's own text — utils/planningDay; they used to be printed
        // twice, as instructions and as "Order notes"), largest vehicle access
        // and the customer's notes.
        deliveryInstructions: (o.delivery_instructions || '').trim(),
        lva: (o.customer_largest_vehicle_access || '').trim(),
        customerNotes: (o.customer_notes || '').trim(),
        hasInstructions: !!((o.delivery_instructions || '').trim() || (o.customer_largest_vehicle_access || '').trim()
          || (o.customer_notes || '').trim()),
        productCode: o.product_code || '',
        productName: o.product_name || '',
        quantity: formatQuantity(o.quantity || 0),
        lineTonnes: o.line_tonnes != null ? formatQuantity(Number(o.line_tonnes)) : '',
        poNumber: rowReference(o),
        supplierRef: o.supplier_reference || '',
        status: o.status || '',
        statusClass: (o.status || '').toLowerCase().replace(/ /g, '-'),
        scheduledTime: o.scheduled_time ? String(o.scheduled_time).slice(0, 5) : '',
        haulier: o.haulier || '',
        driver: o.driver || '',
        vehicle: o.vehicle || ''
      };
    };
    // D#: the lorry day's drop stop (lorry day rules), else the row's drop
    // within its load — whichever the board shows by the row's time.
    const { groups, deliveries } = officeSummaryLayout(orders, {
      toDelivery,
      newRules: lorryDayOn,
      dropNumberOf: (o) => (lorryDayOn
        ? (planByRow.get(String(o.order_line_id)) || {}).dropNumber
        : dropNumbers.get(o.order_line_id))
    });

    const vlPayload = vehicleLoads.map((l) => {
      const effSev = efficiencySeverity(l);
      const fitSev = fitSeverity(l);
      const over = l.effPayload != null && l.totalTonnes > l.effPayload;
      const pct = l.effPayload ? Math.min(100, (l.totalTonnes / l.effPayload) * 100) : 0;
      const eff = l.efficiency != null ? Math.round(l.efficiency) : null;
      const uFitSev = l.isUnknown ? (l.effPayload == null ? 'warn' : (over ? 'danger' : 'ok')) : fitSev;
      const uFitLabel = l.isUnknown
        ? (l.effPayload == null ? 'Set payload' : (over ? 'Over payload' : 'Payload set'))
        : fitLabelFor(l.fit);
      return {
        registration: l.registration,
        multiLoad: l.loadCount > 1,
        loadLabel: `${l.loadIndex + 1}/${l.loadCount}`,
        fitSeverity: uFitSev,
        fitLabel: uFitLabel,
        effSeverity: effSev,
        hasEfficiency: eff != null,
        efficiency: eff,
        ringOffset: eff != null
          ? (RING_CIRC * (1 - Math.min(100, Math.max(0, eff)) / 100)).toFixed(2)
          : RING_CIRC,
        over,
        pct: Math.round(pct),
        totalTonnes: formatQuantity(l.totalTonnes),
        payloadLabel: l.effPayload != null ? formatTonnes(l.effPayload) : 'payload n/a',
        moffettLabel: (l.hasMoffett && l.effPayload != null) ? ' (−1 Moffett)' : '',
        dropLabel: `${l.lineCount} drop${l.lineCount === 1 ? '' : 's'}`,
        supplierLine: `${l.supplier || ''}${l.timeLabel ? ` · ${l.timeLabel}` : ''}`,
        driver: l.driver || null,
        overMsg: over ? `Over payload by ${formatTonnes(l.overBy)}` : '',
        messages: Array.isArray(l.messages) ? l.messages : [],
        doorMsg: (l.doorConfig && l.fit !== 'wont-fit')
          ? `Set doors at: ${l.doorConfig.map(d => `${d}T`).join(', ')}`
          : ''
      };
    });

    const totalTonnes = orders.reduce((sum, o) => {
      const t = Number(o.line_tonnes);
      return sum + (Number.isFinite(t) ? t : (Number(o.quantity) || 0));
    }, 0);
    const distinct = (vals) => new Set(vals.filter(v => v && String(v).trim())).size;

    return {
      deliveries,
      groups,
      dayRules: lorryDayOn ? 'new' : 'old',
      vehicleLoads: vlPayload,
      stats: {
        deliveryCount: orders.length,
        totalTonnes: formatQuantity(totalTonnes),
        loadCount: vehicleLoads.length,
        vehicleCount: distinct(orders.map(o => o.vehicle)),
        driverCount: distinct(orders.map(o => o.driver)),
        haulierCount: distinct(orders.map(o => o.haulier))
      }
    };
  };

  // Recipients come from OfficeSummaryModal: the office, the ticked reps,
  // and anyone added.
  const sendOfficeSummary = async (recipients, { notice = '', sendAs = 'admin' } = {}) => {
    // De-dupe case-insensitively, preserving first-seen casing
    const unique = [...new Map(recipients.map(e => [e.toLowerCase(), e])).values()];

    if (unique.length === 0) {
      setAlertDialog({
        open: true,
        title: 'No Recipients',
        message: 'Please select at least one recipient for the summary.',
        type: 'warning'
      });
      return;
    }

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const bad = unique.filter(e => !emailRe.test(e));
    if (bad.length > 0) {
      setAlertDialog({
        open: true,
        title: 'Invalid Email Address',
        message: `These addresses look invalid:\n${bad.join('\n')}`,
        type: 'warning'
      });
      return;
    }

    setOfficeSummaryModal(prev => ({ ...prev, generating: true }));

    try {
      const response = await fetch(`${API_URL}/api/transport/planning/send-office-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: selectedDate,
          recipients: unique,
          summary: buildOfficeSummaryPayload(),
          notice,
          sendAs
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send summary');
      }

      setOfficeSummaryModal({ open: false, generating: false });
      fetchOfficeSummaryLog(selectedDate);
      setAlertDialog({
        open: true,
        title: 'Summary Sent',
        message: `Transport summary sent successfully to ${unique.length} recipient(s).`,
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to send office summary:', error);
      setOfficeSummaryModal(prev => ({ ...prev, generating: false }));
      setAlertDialog({
        open: true,
        title: 'Error',
        message: `Failed to send summary: ${error.message}`,
        type: 'error'
      });
    }
  };

  // Open supplier selection modal
  const openSupplierSelection = () => {
    // Group orders by supplier and get email from order data. A self-billed PO
    // never goes to a mill (utils/selfBilledPo).
    const supplierData = {};
    millEmailRows(orders).forEach(order => {
      const supplier = order.supplier_name || order.contact_name || 'Unknown';
      if (!supplierData[supplier]) {
        supplierData[supplier] = {
          orderCount: 0,
          email: order.supplier_email || '',
          accountId: order.supplier_account_id || null
        };
      }
      if (!supplierData[supplier].accountId && order.supplier_account_id) {
        supplierData[supplier].accountId = order.supplier_account_id;
      }
      supplierData[supplier].orderCount += 1;
      // Use the first non-empty email found for this supplier
      if (!supplierData[supplier].email && order.supplier_email) {
        supplierData[supplier].email = order.supplier_email;
      }
    });

    // Build supplier selection list
    const supplierList = Object.entries(supplierData).map(([name, data]) => ({
      name,
      key: name,
      orderCount: data.orderCount,
      accountId: data.accountId,
      email: data.email,
      emails: data.email ? data.email.split(/[;,]/).map(e => e.trim()).filter(Boolean) : [],
      include: true
    }));

    // Sort by name
    supplierList.sort((a, b) => a.name.localeCompare(b.name));

    setSupplierSelectionModal({ open: true, suppliers: supplierList, sendToOffice: false });

    // The addresses the send will actually use (Transport Settings → Mill
    // Emails, else the contact record) — shown read-only.
    const ids = supplierList.map(x => x.accountId).filter(Boolean);
    fetch(`${API_URL}/api/transport/planning/paperwork-recipients?accountIds=${ids.join(',')}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.officeEmail) setOfficeEmail(d.officeEmail);
        setSupplierSelectionModal(prev => ({
          ...prev,
          suppliers: prev.suppliers.map(x => {
            const r = x.accountId && d.suppliers?.[x.accountId];
            if (!r || !r.emails?.length) return x;
            return { ...x, emails: r.emails, emailNote: r.source === 'paperwork' ? null : 'From the contact record — set paperwork addresses in Transport Settings → Mill Emails' };
          })
        }));
      })
      .catch(() => {});
  };

  const toggleSupplier = (key, include) => setSupplierSelectionModal(prev => ({
    ...prev, suppliers: prev.suppliers.map(x => x.key === key ? { ...x, include } : x)
  }));

  // Send supplier emails
  const sendSupplierEmails = async ({ notice = '', sendAs = 'admin' } = {}) => {
    const { sendToOffice } = supplierSelectionModal;
    const selectedSuppliers = supplierSelectionModal.suppliers.filter(s => s.include && (sendToOffice || s.emails.length));

    if (selectedSuppliers.length === 0) {
      setAlertDialog({
        open: true,
        title: 'No Suppliers Selected',
        message: 'Please select at least one supplier to send emails to.',
        type: 'warning'
      });
      return;
    }


    setSupplierSelectionModal(prev => ({ ...prev, generating: true }));

    try {
      // Build supplier selections for API
      const supplierSelections = selectedSuppliers.map(s => ({
        supplier: s.name,
        email: s.emails.join(', '),
        include: true
      }));

      const response = await fetch(`${API_URL}/api/transport/planning/send-supplier-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: selectedDate,
          // Each row carries the compartments it loads into ("3, 4", from
          // the load analysis) for the mills that want them — see COMP in
          // routes/transport.js send-supplier-emails.
          orders: millEmailRows(orders).map(o => ({ ...o, compartments: compartmentsFor.get(o.order_line_id) || '' })),
          supplierSelections,
          sendToOffice,
          notice,
          sendAs
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send supplier emails');
      }

      const result = await response.json();

      // Close modal
      setSupplierSelectionModal({ open: false, suppliers: [] });

      // Refresh supplier email log
      fetchSupplierEmailLog(selectedDate);

      // Show success message
      const successCount = result.successCount || result.supplierCount;
      const failedCount = result.failedCount || 0;

      if (failedCount > 0) {
        const failedSuppliers = result.emailResults
          ?.filter(r => !r.success)
          .map(r => `${r.supplier}: ${r.error}`)
          .join('\n') || '';

        setAlertDialog({
          open: true,
          title: 'Emails Partially Sent',
          message: `Sent to ${successCount} supplier(s), ${failedCount} failed.\n\nFailed:\n${failedSuppliers}`,
          type: 'warning'
        });
      } else {
        setAlertDialog({
          open: true,
          title: 'Supplier Emails Sent',
          message: `Order summaries sent successfully to ${successCount} supplier(s).`,
          type: 'success'
        });
      }

    } catch (error) {
      console.error('Failed to send supplier emails:', error);
      setSupplierSelectionModal(prev => ({ ...prev, generating: false }));
      setAlertDialog({
        open: true,
        title: 'Error',
        message: `Failed to send supplier emails: ${error.message}`,
        type: 'error'
      });
    }
  };

  const todayStr = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  })();

  return (
    <div className={`transport-planning${canEdit ? '' : ' read-only-mode'}`}>
      <PlanningControlBar control={control} board="bulk" />
      {!canPlan && (
        <div
          style={{
            background: 'var(--bg-secondary, #f5f5f5)',
            border: '1px solid var(--color-warning, #f59e0b)',
            borderRadius: '6px',
            padding: '10px 14px',
            margin: '0 0 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.9rem'
          }}
        >
          <span className="material-symbols-rounded" style={{ color: 'var(--color-warning, #f59e0b)' }}>visibility</span>
          <span>
            <strong>Read-only.</strong> You don't have permission to edit Transport Planning.
            Ask an admin to grant <em>Transport Planning</em> in User Management if you need to make changes.
          </span>
        </div>
      )}
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Plan and optimise transport routes and schedules</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Coordinate deliveries and manage transport resources efficiently
            </span>
          </span>
        </div>
        
        <div className="search-header-inner">
          <div className="notifications">
            {lastAutoSaved && (
              <HeaderNotice variant="notice-save" icon="check_circle">
                <span className="sent-time"><span>Last auto-saved</span> {formatAutoSaveTime(lastAutoSaved)}</span>
              </HeaderNotice>
            )}
            <TransportEmailNotices
              date={selectedDate}
              supplierLog={supplierEmailLog}
              paperworkLog={paperworkLog}
              officeLog={officeSummaryLog}
              onOpenSends={(kind) => setHistoryModal({ open: true, kind })}
              onOpenPlanHistory={openPlanHistory}
              canSeeHistory={badgeOf(user) !== 'REP'}
            />
          </div>
          <div className="buttons">
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: lockLoading || controlBlock ? 'default' : 'pointer', opacity: controlBlock ? 0.5 : 1 }}
              title={controlBlock || (isLocked
                ? (isPastDate ? 'Unlock this past day to correct haulier, driver or vehicle (needs the unlock password)' : 'Unlock planning (needs the unlock password)')
                : 'Lock planning')}
              onClick={() => {
                if (lockLoading || controlBlock) return;
                if (isLocked) {
                  setUnlockModal({ open: true, password: '', error: null });
                } else {
                  saveLockStatus(selectedDate, true);
                }
              }}
            >
              <div className="check danger light" style={{ fontSize: '22px', pointerEvents: 'none' }}>
                <input
                  type="checkbox"
                  checked={isLocked}
                  readOnly
                />
                <label></label>
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', userSelect: 'none', fontSize: '0.9em', marginRight: '10px' }}>
                <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>
                  {isLocked ? 'lock' : 'lock_open'}
                </span>
                {isLocked ? 'Locked'
                  : (isPastDate && unlockedUntil
                    ? `Correcting until ${new Date(unlockedUntil).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                    : 'Unlocked')}
              </span>
            </div>
            <button
              onClick={openSupplierSelection}
              className="btn-slideout"
              disabled={planFixed || orders.length === 0}
              title="Email order summaries to supplier(s)/ mill(s)"
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">assignment_turned_in</span>
              </div>
              <span className="btn-label">Suppliers/ Mills</span>
            </button>
            <button
              onClick={handleDriverPaperwork}
              className="btn-slideout"
              disabled={planFixed || orders.length === 0}
              title="Generate driver paperwork"
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">local_shipping</span>
              </div>
              <span className="btn-label">Driver Paperwork</span>
            </button>
            <button
              onClick={openOfficeSummary}
              className="btn-slideout"
              disabled={orders.length === 0}
              title="Email a full transport planning summary to the office"
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">groups</span>
              </div>
              <span className="btn-label">Send to Office</span>
            </button>
            <button
              onClick={() => openPlanHistory('email')}
              className={`btn-slideout${badgeOf(user) === 'REP' ? ' btn-disabled' : ''}`}
              disabled={badgeOf(user) === 'REP'}
              data-read-safe
              title={badgeOf(user) === 'REP'
                ? 'Plan history covers every customer on the day, so is not available to rep accounts'
                : 'Everything that happened to this day\'s plan: changes, moves, times, locks and emails sent'}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">history</span>
              </div>
              <span className="btn-label">Plan History</span>
            </button>
          </div>
        </div>
        <div className={`snapshot-banner-wrapper ${isSnapshot ? 'visible' : ''}`}>
          <div className="snapshot-banner">
            <span className="material-symbols-rounded">info</span>
            <span>
              You are viewing a snapshot of the transport plan for this day. For actual delivered and supplied quantities, check individual customer orders.
            </span>
          </div>
        </div>
      </div>

      {/* Date Selector (collapsible) */}
      <button
        className="tp-section-toggle date-picker-toggle"
        onClick={() => setDatePickerCollapsed(c => !c)}
        title={datePickerCollapsed ? 'Show date picker' : 'Hide date picker'}
      >
        <span className="material-symbols-rounded">{datePickerCollapsed ? 'chevron_right' : 'expand_more'}</span>
        <span className="tp-section-toggle-title">Date</span>
        <span className="tp-section-toggle-value">{formatDate(selectedDate).replace('\n', ' ')}</span>
      </button>
      {!datePickerCollapsed && (
      <div className="date-selector-wrapper">
        <button className="date-scroll-btn date-scroll-left" onClick={() => scrollDates('left')} title="Earlier dates">
          <span className="material-symbols-rounded">chevron_left</span>
        </button>
        <div className="date-selector" ref={dateSelectorRef}>
          {(() => {
            const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const groups = {};
            dates.forEach(date => {
              const key = date.slice(0, 7);
              if (!groups[key]) groups[key] = [];
              groups[key].push(date);
            });
            return Object.entries(groups).map(([monthKey, monthDates]) => {
              const d = new Date(monthKey + '-01T00:00:00');
              const monthLabel = `${monthNames[d.getMonth()].toUpperCase()} ${d.getFullYear()}`;
              return (
                <div key={monthKey} className="month-group">
                  <div className="month-label">{monthLabel}</div>
                  <div className="month-dates">
                    {monthDates.map(date => {
                      const dow = new Date(date + 'T00:00:00').getDay();
                      const isWeekend = dow === 0 || dow === 6;
                      const classes = ['date-btn',
                        selectedDate === date ? 'active' : '',
                        date === todayStr ? 'today' : '',
                        isWeekend ? 'weekend' : ''
                      ].filter(Boolean).join(' ');
                      // Warning dot when this day has any job with unacknowledged
                      // changes. The currently-selected date reads from the live
                      // orders so the dot clears as soon as changes are acknowledged;
                      // other dates come from the fetched set.
                      const hasChanges = date === selectedDate
                        ? orders.some(o => o.has_changes)
                        : datesWithChanges.has(date);
                      return (
                        <button key={date} className={classes} onClick={() => setSelectedDate(date)}>
                          {hasChanges && <span className="date-change-dot" title="Unacknowledged changes on this day" />}
                          {formatDate(date).split('\n').map((line, i) => (
                            <div key={i} className={i === 0 ? 'date-label' : 'date-value'}>{line}</div>
                          ))}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
        <button className="date-scroll-btn date-scroll-right" onClick={() => scrollDates('right')} title="Later dates">
          <span className="material-symbols-rounded">chevron_right</span>
        </button>
      </div>
      )}

      {/* Hidden element for measuring text widths */}
      <span ref={measureRef} style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap', fontSize: 'var(--font-md)' }} />

      {/* Orders Table */}
      <div className="transport-content" style={{ position: "relative", width: "100%" }}>
        {loading ? (
          <LoadingSpinner />
        ) : orders.length === 0 ? (
          <div className="no-orders">
            <span className="material-symbols-rounded">local_shipping</span>
            <p>No deliveries scheduled for {formatDate(selectedDate).replace('\n', ' ')}</p>
          </div>
        ) : (
          <>
          {/* Isolate-driver banner — stays pinned above the scroll */}
          {isolatedDriver !== null && (
            <div className="isolate-banner">
              <span className="material-symbols-rounded">filter_alt</span>
              <span>
                Showing only <strong>{isolatedDriver === '' ? 'Unassigned' : isolatedDriver}</strong>
                {isolatedDriver !== '' ? ' + unassigned' : ''}
              </span>
              <button className="isolate-clear" onClick={() => setIsolatedDriver(null)}>
                <span className="material-symbols-rounded">close</span> Clear
              </button>
            </div>
          )}
          {showOnlyOptionPlanned && (
            <div className="isolate-banner">
              <span className="material-symbols-rounded">event_repeat</span>
              <span>Showing only orders <strong>planned today with option dates</strong></span>
              <button className="isolate-clear" onClick={() => setShowOnlyOptionPlanned(false)}>
                <span className="material-symbols-rounded">close</span> Clear
              </button>
            </div>
          )}

          <div className="transport-scroll" ref={transportScrollRef}>
          {/* Day summary — collapsible rollup of the day's deliveries */}
          <div className={`day-summary-section ${daySummaryCollapsed ? 'collapsed' : ''}`}>
            <button
              className="tp-section-toggle"
              onClick={() => setDaySummaryCollapsed(c => !c)}
              title={daySummaryCollapsed ? 'Show day summary' : 'Hide day summary'}
            >
              <span className="material-symbols-rounded">{daySummaryCollapsed ? 'chevron_right' : 'expand_more'}</span>
              <span className="tp-section-toggle-title">Day Summary</span>
              <span className="tp-section-toggle-value">{daySummary.orders} orders · {formatTonnes(daySummary.totalTonnes)}</span>
            </button>
            {!daySummaryCollapsed && (
            <div className="day-summary-bar">
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.orders}</span>
                <span className="day-stat-label">Orders</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{formatQuantity(daySummary.totalTonnes)}<span className="day-stat-unit"> T</span></span>
                <span className="day-stat-label">Total Weight</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.addresses}</span>
                <span className="day-stat-label">Delivery Addresses</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.suppliers}</span>
                <span className="day-stat-label">Suppliers/ Mills</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.ourVehicles}</span>
                <span className="day-stat-label">Our Vehicles</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.thirdPartyDrivers}</span>
                <span className="day-stat-label">3rd Party Drivers</span>
              </div>
              {lorryDayOn && (
                <>
                  <div className="day-stat" title="Vehicle, collection time, drop time and compartments set (or carried by someone else)">
                    <span className="day-stat-value">{planCounts.planned}</span>
                    <span className="day-stat-label">Planned</span>
                  </div>
                  <div className="day-stat" title="On a vehicle, something still to set">
                    <span className="day-stat-value">{planCounts.part}</span>
                    <span className="day-stat-label">Part-planned</span>
                  </div>
                  <div className="day-stat" title="Not on a vehicle yet">
                    <span className="day-stat-value">{planCounts.unplanned}</span>
                    <span className="day-stat-label">Not planned</span>
                  </div>
                </>
              )}
            </div>
            )}
          </div>

          {!lorryDayOn && vehicleLoads.length > 0 && (
            <div className={`vehicle-loads-wrap ${vehicleLoadsCollapsed ? 'collapsed' : ''}`}>
              <button
                className="vehicle-loads-toggle"
                onClick={() => setVehicleLoadsCollapsed(c => !c)}
                title={vehicleLoadsCollapsed ? 'Show vehicle summaries' : 'Hide vehicle summaries'}
              >
                <span className="material-symbols-rounded vl-chevron">{vehicleLoadsCollapsed ? 'chevron_right' : 'expand_more'}</span>
                <span className="vl-toggle-title">Vehicle Summaries</span>
                <span className="vl-toggle-count">{vehicleLoads.length}</span>
              </button>
              {!vehicleLoadsCollapsed && (
              <div className="vehicle-loads">
              {vehicleLoads.map((l) => {
                // Left border + Fits/Won't-fit badge follow FIT; ring + bar follow EFFICIENCY.
                const fitSev = fitSeverity(l);
                const effSev = efficiencySeverity(l);
                const over = l.effPayload != null && l.totalTonnes > l.effPayload;
                // The "Unknown" vehicle carries no compartment spec — only a
                // payload — so it shows payload status rather than a fit verdict.
                const fitLabel = l.isUnknown
                  ? (l.effPayload == null ? 'Set payload' : (over ? 'Over payload' : 'Payload set'))
                  : l.fit === 'ok' ? 'Fits'
                  : l.fit === 'too-many' ? 'Too many drops'
                  : l.fit === 'wont-fit' ? "Won't fit"
                  : l.fit === 'reorder' ? 'Re-sequence'
                  : 'No compartment spec';
                const cardSev = l.isUnknown
                  ? (l.effPayload == null ? 'warn' : (over ? 'danger' : 'ok'))
                  : fitSev;
                const pct = l.effPayload ? Math.min(100, (l.totalTonnes / l.effPayload) * 100) : 0;
                const labelFor = (idx) => {
                  const r = l.rows[idx];
                  return r ? (rowReference(r) || r.customer_name || `Drop ${idx + 1}`) : '?';
                };
                const suggestion = l.suggestedOrder ? l.suggestedOrder.map(labelFor).join(' → ') : null;
                const ringColor = SEVERITY_COLOR[effSev];
                return (
                  <div
                    key={loadKey(l)}
                    className={`vl-card vl-${cardSev} vl-clickable`}
                    onClick={() => setLoadModal({ open: true, key: loadKey(l) })}
                    title={l.isUnknown ? 'Set the payload for this driver’s vehicle' : 'View load details'}
                  >
                    <div className="vl-head">
                      <div className="vl-head-info">
                        <div className="vl-reg-row">
                          <span className="vl-reg">
                            {/* Registry entry for vehicle is idKind 'object' — VehicleModal
                                renders from the record, it does not fetch one. Unknown-vehicle
                                cards carry a synthetic spec with no id, so they stay plain. */}
                            <EntityLink type="vehicle" id={l.isUnknown ? null : l.vehicle} variant="badge">
                              {l.registration}
                            </EntityLink>
                          </span>
                          {l.loadCount > 1 && <span className="vl-load-num">{l.loadIndex + 1}/{l.loadCount}</span>}
                          <span className={`vl-chip vl-chip-${cardSev}`}>{fitLabel}</span>
                        </div>
                        <div className="vl-supplier" title={l.supplier}>
                          {l.supplier}{l.timeLabel ? ` · ${l.timeLabel}` : ''}
                        </div>
                        {l.driver && (
                          <div className="vl-driver">
                            <span className="material-symbols-rounded">person</span>
                            <EntityLink type="driver" id={driverIdFor(l.driver)}>{l.driver}</EntityLink>
                          </div>
                        )}
                      </div>
                      {l.efficiency != null && (
                        <ProgressRing percentage={l.efficiency} color={ringColor} size={48} />
                      )}
                    </div>
                    <div className={`vl-bar ${over ? 'vl-bar-over' : `vl-bar-${effSev}`}`}>
                      <div className="vl-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="vl-meta">
                      <strong className={over ? 'vl-over-text' : ''}>{formatQuantity(l.totalTonnes)}</strong>
                      {' / '}
                      {l.effPayload != null ? formatTonnes(l.effPayload) : 'payload n/a'}
                      {l.hasMoffett && l.effPayload != null ? ' (−1 Moffett)' : ''}
                      {' · '}{l.lineCount} drop{l.lineCount === 1 ? '' : 's'}
                    </div>
                    {over && <div className="vl-msg vl-msg-danger">Over payload by {formatTonnes(l.overBy)}</div>}
                    {l.messages.map((m, i) => <div key={i} className="vl-msg vl-msg-danger">{m}</div>)}
                    {suggestion && <div className="vl-msg vl-msg-warn">Suggested drop order: {suggestion}</div>}
                    {l.doorConfig && l.fit !== 'wont-fit' && (
                      <div className="vl-msg vl-msg-muted">Set doors at: {l.doorConfig.map(d => `${d}T`).join(', ')}</div>
                    )}
                  </div>
                );
              })}
              </div>
              )}
            </div>
          )}

          {/* Bulk actions: appears with a selection (tick, drag, or shift-click). */}
          {selectedRows.size > 0 && (
            <div className="bulk-actions-bar">
              <div className="bulk-actions-info">
                <span className="bulk-actions-title">Bulk Actions:</span>
                <span>{selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''} selected</span>
              </div>
              <div className="bulk-actions-buttons slideout">
                <div>
                  <button data-write-action className="btn-slideout" onClick={() => setBulkEditOpen(true)}
                    disabled={!!bulkEditBlocked} title={bulkEditBlocked || 'Set haulier, driver, vehicle or time on every selected row'}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">edit_note</span></div>
                    <span className="btn-label">Edit {selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''}</span>
                  </button>
                </div>
                <div>
                  <button data-read-safe className="btn-slideout" onClick={() => setSelectedRows(new Set())}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                    <span className="btn-label">Clear</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <table className={`transport-table${lorryDayOn ? ' lorry-day-on' : ''}`} style={{ position: "relative", width: "100%" }} onContextMenu={ctxMenu.onTableContextMenu} data-time-grid>
            <thead>
              <tr>
                <th className="col-select">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="select-all-transport"
                      checked={selectedRows.size === orders.length && orders.length > 0}
                      onChange={toggleSelectAll}
                    />
                    <label htmlFor="select-all-transport" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                  </label>
                </th>
                <th className="col-num">
                  {lorryDayOn && boardGroups && boardGroups.length > 0 ? (() => {
                    const allShut = boardGroups.every(g => collapsedNow.has(g.key));
                    return (
                      <button type="button" className="btn-icon ld-collapse ld-collapse-all"
                        title={allShut ? 'Show every group’s rows' : 'Hide every group’s rows (their summaries stay)'}
                        aria-label={allShut ? 'Show every group’s rows' : 'Hide every group’s rows'}
                        onClick={() => setAllCollapsed(!allShut)}>
                        <span className="material-symbols-rounded">{allShut ? 'unfold_more' : 'unfold_less'}</span>
                      </button>
                    );
                  })() : '#'}
                </th>
                <th className="col-supplier">SUP/ MILL</th>
                <th className="col-customer">Customer</th>
                <th className="col-instructions">Del. Inst.</th>
                <th className="col-product">Product</th>
                <th className="col-qty">{isSnapshot ? 'P. Qty' : 'Qty'}</th>
                <th className="col-po">REFS</th>
                <th className="col-status">Status</th>
                <th className="col-time">{lorryDayOn ? 'Collect → Drop' : 'Time'}</th>
                <th className="col-haulier" style={{ minWidth: colWidths.haulier }}>Haulier</th>
                <th className="col-driver" style={{ minWidth: colWidths.driver }}>Driver</th>
                <th className="col-vehicle" style={{ minWidth: colWidths.vehicle }}>Vehicle</th>
                <th className="col-actions" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {lorryDayOn
                ? boardGroups.map(g => (
                  <React.Fragment key={g.key}>
                    {renderGroupHeader(g)}
                    {!collapsedNow.has(g.key) && g.rows.map(o => renderPlanningRow(o, false, g))}
                  </React.Fragment>
                ))
                : visiblePlanned.map(o => renderPlanningRow(o, false))}
              {visibleCandidates.length > 0 && (
                <tr className="option-section-header" onClick={() => setOptionSectionCollapsed(c => !c)}>
                  <td colSpan={14}>
                    <span className="material-symbols-rounded osh-chevron">{optionSectionCollapsed ? "chevron_right" : "expand_more"}</span>
                    <span className="osh-title">Option-date orders — not planned this day</span>
                    <span className="osh-badge">{visibleCandidates.length}</span>
                  </td>
                </tr>
              )}
              {!optionSectionCollapsed && visibleCandidates.map(o => renderPlanningRow(o, true))}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>

      {/* Changes Modal */}
      <Modal
        isOpen={changesModal.open}
        onClose={() => setChangesModal({ open: false, order: null, changes: [], loading: false, hasUnacknowledged: false })}
        title={changesModal.order
          ? `${changesModal.hasUnacknowledged ? 'Recent Changes' : 'Change History'}: ${rowReference(changesModal.order)}`
          : (changesModal.hasUnacknowledged ? 'Recent Changes' : 'Change History')
        }
        size="lg"
        className="transport-changes-modal"
        footer={changesModal.hasUnacknowledged ? (
          <div className="panel-nav-buttons">
            <div>
              <button
                className="btn-slideout"
                onClick={() => {
                  if (changesModal.order) {
                    dismissModified(changesModal.order);
                  }
                  setChangesModal({ open: false, order: null, changes: [], loading: false, hasUnacknowledged: false });
                }}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">done_all</span>
                </div>
                <span className="btn-label">Acknowledge</span>
              </button>
            </div>
          </div>
        ) : null}
      >
        {changesModal.loading ? (
          <div className="loading-state">
            <span className="material-symbols-rounded spinning">sync</span>
            <span>Loading changes...</span>
          </div>
        ) : changesModal.changes.length === 0 ? (
          <div className="empty-state">
            <span className="material-symbols-rounded">history</span>
            <span>No changes recorded{changesModal.hasUnacknowledged ? ' since last acknowledgment' : ''}</span>
          </div>
        ) : (
          <div className="history-timeline">
            {changesModal.changes.map((change, idx) => {
              // Parse context if available
              let context = null;
              if (change.context) {
                try {
                  context = typeof change.context === 'string'
                    ? JSON.parse(change.context)
                    : change.context;
                } catch (e) {
                  // Ignore parse errors
                }
              }

              const hasValues = change.old_value !== null || change.new_value !== null;
              const isExpanded = expandedChanges.has(idx);

              return (
                <div key={idx} className={`timeline-entry ${isExpanded ? 'expanded' : ''}`}>
                  <div className="timeline-marker">
                    <div className="marker-dot"></div>
                    {idx < changesModal.changes.length - 1 && <div className="marker-line"></div>}
                  </div>
                  <div className="timeline-content">
                    <div
                      className="entry-header"
                      onClick={() => { if (hasValues) toggleChangeEntry(idx); }}
                      style={{ cursor: hasValues ? 'pointer' : 'default' }}
                    >
                      <div className="entry-meta">
                        <span className="action-badge action-update">{change.source_label}</span>
                        <span className="entry-field-group">
                          <span className="field-name">{change.field_display || change.field_name}</span>
                          {context?.product && (
                            <span className="entry-product">({context.product})</span>
                          )}
                        </span>
                        <span className="entry-date">{formatChangeTime(change.created_at)}</span>
                      </div>
                      <div className="entry-user">
                        <span className="material-symbols-rounded">person</span>
                        <span>{change.changed_by_name}</span>
                      </div>
                      {hasValues && (
                        <div className="entry-expand">
                          <span className="material-symbols-rounded">expand_more</span>
                        </div>
                      )}
                    </div>
                    {isExpanded && hasValues && (
                      <div className="entry-changes">
                        <div className="changes-list">
                          <div className="change-item">
                            <div className="field-values">
                              <span className="old-value">{formatChangeValue(change.old_value, change.field_name)}</span>
                              <span className="arrow">→</span>
                              <span className="new-value">{formatChangeValue(change.new_value, change.field_name)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Unlock Modal */}
      {unlockModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setUnlockModal({ open: false, password: '', error: null })}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header"  style={{paddingBottom: "var(--spacing-xl)"}}>
              <h3 style={{ margin: "0" }}>Unlock Transport Plan</h3>
              <button className="modal-close" onClick={() => setUnlockModal({ open: false, password: '', error: null })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: "0 var(--spacing-xl)" }}>
              <p>
                {isPastDate
                  ? 'This day has passed. Enter the transport unlock password to correct who delivered — haulier, driver and vehicle — for the next 30 minutes. Nothing else on a past day changes.'
                  : 'Enter the transport unlock password to unlock editing.'}
              </p>
              <p className="text-muted" style={{ fontSize: '0.85em' }}>
                Forgotten it? Amy, Matt, Jade or an admin can look it up in Transport Settings → Unlock Password.
              </p>
              {unlockModal.error && <p className="error-message">{unlockModal.error}</p>}
              <input
                type="password"
                className="form-input"
                placeholder="Password"
                value={unlockModal.password}
                onChange={e => setUnlockModal(prev => ({ ...prev, password: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleUnlock()}
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={handleUnlock}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">lock_open</span>
                </div>
                <span className="btn-label">Unlock</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Table context menu (right-click) */}
      {bulkEditOpen && (
        <PlanningBulkEditModal
          count={selectedRows.size}
          jobCount={0}
          hauliers={hauliers}
          drivers={drivers}
          vehicles={vehicles}
          onApply={applyBulkEdit}
          onClose={() => setBulkEditOpen(false)}
        />
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={(row) => {
          const { bulk, count, ids, rows: targetRows } = ctxMenu;
          const optionDayRows = targetRows.filter(isOptionCandidate);
          const selDrivers = bulk ? [...new Set(targetRows.map(o => (o.driver || '').trim()))] : [];
          const isolateTarget = bulk ? (selDrivers[0] ?? '') : (row.driver || '').trim();
          return [
            goToOrderAction(row),
            optionDayRows.length > 0 && {
              key: 'plan-on-day',
              icon: 'event_available',
              label: optionDayRows.length > 1 ? `Plan ${optionDayRows.length} on this day` : 'Plan on this day',
              disabled: planFixed,
              onClick: () => planOnThisDay(optionDayRows)
            },
            {
              key: 'send-top',
              icon: 'vertical_align_top',
              label: bulk ? `Send ${count} rows to top` : 'Send to top',
              disabled: lorryDayOn || planFixed || count === 0,
              title: lorryDayOn ? ORDER_BY_TIME : undefined,
              onClick: () => sendIdsToTop(ids)
            },
            {
              key: 'send-to',
              icon: 'low_priority',
              label: bulk ? `Send ${count} rows to…` : 'Send to…',
              disabled: lorryDayOn || planFixed || count === 0,
              title: lorryDayOn ? ORDER_BY_TIME : undefined,
              onClick: () => setMoveToModal({ open: true, value: '', targetIds: ids })
            },
            {
              key: 'move-date',
              icon: 'event',
              label: bulk ? `Move ${count} rows to date…` : 'Move to date…',
              // A self-billed PO has no order to move: its date is changed on the PO.
              // Nor a redelivery: its date is changed in its Redelivery window.
              disabled: planFixed || count === 0 || targetRows.every(o => o.is_self_billed || o.is_redelivery),
              title: targetRows.some(o => o.is_self_billed) ? SELF_BILLED_DATE_REASON
                : (targetRows.some(o => o.is_redelivery) ? REDELIVERY_DATE_REASON : undefined),
              onClick: () => openMoveDate(targetRows.filter(o => !o.is_self_billed && !o.is_redelivery))
            },
            {
              key: 'change-qty',
              icon: 'scale',
              label: 'Change quantity…',
              // One line at a time; a customer-order line (not a haulage job).
              disabled: planFixed || count !== 1 || !targetRows[0]?.order_line_id || !targetRows[0]?.id || !!targetRows[0]?.is_redelivery,
              title: targetRows[0]?.is_redelivery ? REDELIVERY_QTY_REASON : undefined,
              onClick: () => setChangeQtyRow(targetRows[0])
            },
            {
              key: 'move-board',
              icon: 'shopping_bag',
              label: bulk ? `Move ${count} rows to Bag board` : 'Move to Bag board',
              disabled: planFixed || count === 0 || targetRows.some(o => !o.order_line_id || o.is_self_billed || o.is_redelivery),
              title: targetRows.some(o => o.is_self_billed) ? SELF_BILLED_BOARD_REASON
                : (targetRows.some(o => o.is_redelivery) ? REDELIVERY_BOARD_REASON : undefined),
              onClick: async () => {
                const lineIds = targetRows.map(o => o.order_line_id).filter(Boolean);
                await Promise.all(lineIds.map(id => fetch(`${API_URL}/api/transport/planning/set-board-type`, {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ orderLineId: id, board: 'bag' })
                }).catch(e => console.error('Move to bag board failed:', e))));
                fetchOrders();
              }
            },
            // One row; a selection gets this from the bulk actions instead.
            ...(bulk ? [] : [{
              key: 'split-run',
              icon: 'call_split',
              label: 'Split from run',
              disabled: lorryDayOn || planFixed || !row.run_key,
              title: lorryDayOn ? RUNS_RETIRED : (row.run_key ? undefined : 'Not part of a combined run'),
              onClick: () => splitFromRun([row])
            }]),
            {
              key: 'isolate',
              icon: 'filter_alt',
              label: bulk ? 'Isolate driver' : (isolateTarget ? `Isolate ${isolateTarget}` : 'Isolate unassigned'),
              disabled: planFixed || (bulk && selDrivers.length > 1),
              onClick: () => setIsolatedDriver(isolateTarget)
            },
            // Refused — redeliver… / Edit redelivery… (hooks/useBoardRedeliver).
            ...rowRedeliver.actionsFor(row),
            // Archive order… / Archive PO… — the same pair as the three-dots menu.
            ...rowArchive.actionsFor(row)
          ];
        }}
        bulkActions={(ids) => [
          {
            key: 'bulk-edit',
            icon: 'edit_note',
            label: `Set haulier / driver / vehicle / time on ${ids.size} rows…`,
            disabled: !!bulkEditBlocked,
            title: bulkEditBlocked || undefined,
            onClick: () => setBulkEditOpen(true)
          },
          {
            key: 'group',
            icon: 'layers',
            label: `Group ${ids.size} rows`,
            disabled: lorryDayOn || planFixed,
            title: lorryDayOn ? ORDER_BY_TIME : undefined,
            onClick: () => groupIds(new Set(ids))
          },
          {
            key: 'combine',
            icon: 'merge',
            label: `Combine ${ids.size} rows into one run`,
            disabled: lorryDayOn || planFixed,
            title: lorryDayOn ? RUNS_RETIRED : undefined,
            onClick: () => setCombineRunModal({ open: true, value: '', targetIds: new Set(ids) })
          },
          {
            key: 'split-run',
            icon: 'call_split',
            label: `Split ${ids.size} rows from their run`,
            disabled: lorryDayOn || planFixed || !orders.some(o => ids.has(o.order_line_id) && o.run_key),
            title: lorryDayOn ? RUNS_RETIRED : undefined,
            onClick: () => splitFromRun(orders.filter(o => ids.has(o.order_line_id)))
          }
        ]}
        actions={[
          {
            key: 'auto-group',
            icon: 'group_work',
            label: 'Auto-group by driver',
            disabled: lorryDayOn || planFixed,
            title: lorryDayOn ? 'Lorry day planning is on: the board is already grouped by haulier, driver and vehicle' : undefined,
            onClick: autoGroupByDriver
          },
          {
            key: 'colours',
            icon: 'palette',
            label: 'Toggle colours',
            active: driverColorsOn,
            onClick: () => setDriverColorsOn(v => !v)
          },
          {
            key: 'option-planned',
            icon: 'event_repeat',
            label: 'Show only planned-with-options',
            active: showOnlyOptionPlanned,
            onClick: () => setShowOnlyOptionPlanned(v => !v)
          },
          isolatedDriver !== null && {
            key: 'clear-isolate',
            icon: 'filter_alt_off',
            label: 'Clear isolate',
            onClick: () => setIsolatedDriver(null)
          }
        ]}
      />

      {/* Move to… row-number prompt */}

      {moveToModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setMoveToModal({ open: false, value: '', targetIds: null })}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ paddingBottom: 'var(--spacing-xl)' }}>
              <h3 style={{ margin: '0' }}>Move {moveToModal.targetIds ? moveToModal.targetIds.size : 0} Row{(moveToModal.targetIds ? moveToModal.targetIds.size : 0) !== 1 ? 's' : ''}</h3>
              <button className="modal-close" onClick={() => setMoveToModal({ open: false, value: '', targetIds: null })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 var(--spacing-xl)' }}>
              <p>Enter a row number — the {(moveToModal.targetIds ? moveToModal.targetIds.size : 0) > 1 ? 'rows are grouped and moved' : 'row is moved'} directly beneath it.</p>
              <input
                type="number"
                min="1"
                max={plannedCount}
                className="form-input"
                placeholder={`Row number (1–${plannedCount})`}
                value={moveToModal.value}
                onChange={e => setMoveToModal(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && applyMoveTo()}
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={applyMoveTo}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">low_priority</span>
                </div>
                <span className="btn-label">Move</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Combine rows into one run (shared collection time) */}
      {combineRunModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setCombineRunModal({ open: false, value: '', targetIds: null })}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ paddingBottom: 'var(--spacing-xl)' }}>
              <h3 style={{ margin: '0' }}>Combine {combineRunModal.targetIds ? combineRunModal.targetIds.size : 0} Rows into One Run</h3>
              <button className="modal-close" onClick={() => setCombineRunModal({ open: false, value: '', targetIds: null })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 var(--spacing-xl)' }}>
              <p>These rows become one run for one vehicle: collected in board order, then delivered. Each keeps its own collection time — say 06:00 at one mill and 07:00 at the next — unless you enter one here for them all.</p>
              <input
                type="text"
                className="form-input"
                placeholder="Optional — e.g. 9, 14:30 or 2pm"
                maxLength={7}
                value={combineRunModal.value}
                onChange={e => setCombineRunModal(prev => ({ ...prev, value: e.target.value }))}
                onBlur={e => {
                  const n = normalizeTimeValue(e.target.value);
                  if (n) setCombineRunModal(prev => ({ ...prev, value: n }));
                }}
                // Enter stores the typed time and combines in one go. The value
                // is normalised into the field first so an invalid entry is left
                // tidied up (and visible) when the guard sends it back.
                onKeyDown={e => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  const n = normalizeTimeValue(e.currentTarget.value);
                  if (n) setCombineRunModal(prev => ({ ...prev, value: n }));
                  applyCombineRun();
                }}
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={applyCombineRun}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">merge</span>
                </div>
                <span className="btn-label">Combine</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Move to another delivery date */}
      {moveDateModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setMoveDateModal({ open: false, value: '', orderIds: [], count: 0, optionDates: [] })}>
          <div className="modal-content modal-sm move-delivery-date-modal" style={{ height: 'fit-content', maxHeight: '90vh', minWidth: '400px', maxWidth: '90vw', width: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ paddingBottom: 'var(--spacing-md)' }}>
              <h3 style={{ margin: '0' }}>Move to Delivery Date</h3>
              <button className="modal-close" onClick={() => setMoveDateModal({ open: false, value: '', orderIds: [], count: 0, optionDates: [] })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: 'var(--spacing-md) var(--spacing-xl) var(--spacing-xl)' }}>
              <p style={{ fontSize: '0.9em', margin: '0 0 var(--spacing-sm)' }}>
                Move {moveDateModal.count} row{moveDateModal.count === 1 ? '' : 's'}
                {moveDateModal.orderIds.length !== moveDateModal.count ? ` (${moveDateModal.orderIds.length} order${moveDateModal.orderIds.length === 1 ? '' : 's'})` : ''}
                {' '}to a new delivery date.
              </p>
              <p style={{ fontSize: '0.75em', color: 'var(--text-muted)' }}>
                This updates the delivery date on the whole order and its purchase order(s).
              </p>
              <DateInput
                value={moveDateModal.value}
                onChange={e => setMoveDateModal(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && applyMoveDate()}
              />
              {moveDateModal.optionDates.length > 0 && (
                <div className="move-date-options">
                  <div className="move-date-options-label">Or select from option dates for this order:</div>
                  <div className="move-date-options-list">
                    {moveDateModal.optionDates.map((od) => (
                      <button
                        key={od.date}
                        type="button"
                        className={`move-date-option ${moveDateModal.value === od.date ? 'active' : ''}`}
                        onClick={() => setMoveDateModal(prev => ({ ...prev, value: od.date }))}
                      >
                        {new Date(od.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                        <span className="move-date-option-pref">P{od.preference}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={applyMoveDate} disabled={!moveDateModal.value}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">event</span>
                </div>
                <span className="btn-label">Move</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Customer / Order Notes */}
      {notesModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setNotesModal({ open: false, order: null })}>
          <div className="modal-content modal-sm transport-notes-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ paddingBottom: 'var(--spacing-md)' }}>
              <h3 style={{ margin: '0' }}>Customer / Order Notes</h3>
              <button className="modal-close" onClick={() => setNotesModal({ open: false, order: null })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 var(--spacing-xl) var(--spacing-xl)' }}>
              {notesModal.order && (
                <div className="notes-subtitle">
                  {notesModal.order.customer_name}
                  {rowReference(notesModal.order) ? ` · ${rowReference(notesModal.order)}` : ''}
                </div>
              )}
              {notesModal.order && (notesModal.order.customer_notes?.trim() || notesModal.order.order_notes?.trim() || notesModal.order.customer_largest_vehicle_access?.trim()) ? (
                <>
                  {notesModal.order.customer_notes?.trim() && (
                    <div className="notes-block">
                      <div className="notes-block-label">Customer Account</div>
                      <div className="notes-block-body">{notesModal.order.customer_notes}</div>
                    </div>
                  )}
                  {/* What will physically get in. Internal, like the notes. */}
                  {notesModal.order.customer_largest_vehicle_access?.trim() && (
                    <div className="notes-block">
                      <div className="notes-block-label">Largest Vehicle Access</div>
                      <div className="notes-block-body">{notesModal.order.customer_largest_vehicle_access}</div>
                    </div>
                  )}
                  {notesModal.order.order_notes?.trim() && (
                    <div className="notes-block">
                      <div className="notes-block-label">Order</div>
                      <div className="notes-block-body">{notesModal.order.order_notes}</div>
                    </div>
                  )}
                </>
              ) : (
                <p className="notes-empty">No notes recorded on the customer account or order.</p>
              )}
            </div>
          </div>
        </div>, document.body
      )}

      {/* Paperwork Validation Modal */}
      {paperworkModal.open && !paperworkModal.generating && createPortal(
        <div className="modal-overlay" onClick={() => setPaperworkModal({ open: false, warnings: [], generating: false, progress: 0 })}>
          <div className="modal-content modal-lg driver-paperwork-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <h3 style={{ margin: '0' }}>Driver Paperwork</h3>
              <button className="modal-close" onClick={() => setPaperworkModal({ open: false, warnings: [], generating: false, progress: 0 })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 var(--spacing-xl) var(--spacing-xl)' }}>
              {paperworkModal.warnings.length > 0 ? (
                <>
                  <div className="warning-banner">
                    <span className="material-symbols-rounded">warning</span>
                    <span>The following orders have missing information:</span>
                  </div>
                  <div className="warnings-list">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>PO #</th>
                          <th>Customer</th>
                          <th>Product</th>
                          <th style={{ textAlign: 'center' }}>Qty</th>
                          <th>Issues</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paperworkModal.warnings.map((w, idx) => (
                          <tr key={idx}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {w.order ? <RowReferenceLink order={w.order} /> : w.po_number}
                            </td>
                            <td>
                              <span className="badge-text-row">
                                {w.customer_account_no && (
                                  <span className="account-badge">
                                    <EntityLink type="contact" id={w.customer_account_id} variant="badge">
                                      {w.customer_account_no}
                                    </EntityLink>
                                  </span>
                                )}
                                <EntityLink type="contact" id={w.customer_account_id}>{w.customer}</EntityLink>
                              </span>
                            </td>
                            <td>
                              <span className="badge-text-row">
                                {w.product_code && (
                                  <span className="product-badge">
                                    <EntityLink type="inventory_item" id={w.inventory_item_id} variant="badge">
                                      {w.product_code}
                                    </EntityLink>
                                  </span>
                                )}
                                <span>{w.product}</span>
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>{w.quantity != null ? (formatQuantity(Number(w.quantity)) || '-') : '-'}</td>
                            <td className="warning-issues">{w.issues.join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="warning-note">You can continue anyway, or return to fix these issues.</p>
                </>
              ) : (
                <p>All orders are complete and ready for paperwork generation.</p>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-slideout"
                onClick={() => setPaperworkModal({ open: false, warnings: [], generating: false, progress: 0 })}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">arrow_back</span>
                </div>
                <span className="btn-label">Return to Planning</span>
              </button>
              <button
                className="btn-slideout"
                onClick={openHaulierSelection}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">description</span>
                </div>
                <span className="btn-label">Continue</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Paperwork Progress Modal */}
      {paperworkModal.generating && createPortal(
        <div className="modal-overlay">
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generating Driver Paperwork</h3>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="progress-spinner">
                <span className="material-symbols-rounded spinning-anti">sync</span>
              </div>
              <p>Generating driver paperwork and despatch notes...</p>
              <p className="progress-note">This may take a moment.</p>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Haulier Selection Modal */}
      {/* Driver paperwork — shared with the Bag board (PaperworkSendModal). */}
      <PaperworkSendModal
        user={user}
        isOpen={haulierSelectionModal.open}
        onClose={() => setHaulierSelectionModal({ open: false, hauliers: [] })}
        title="Send Driver Paperwork"
        intro="Each driver gets one email with their own deliveries: a top sheet and a despatch note per delivery."
        warning={[
          unplannedSelfBilledWarning(orders, 'driver'),
          fallbackWarning(haulierSelectionModal.hauliers, { sendToOffice: !!haulierSelectionModal.sendToOffice }),
          // Over-filled lorries are allowed — said here before it goes out.
          lorryDayOn ? overfillWarning(lorryDays) : null
        ]}
        sendNoun="driver"
        settingsHint="Transport Settings"
        recipients={haulierSelectionModal.hauliers.map(h => ({
          key: h.key,
          label: h.isDriver ? <EntityLink type="driver" id={h.driverId}>{h.label}</EntityLink> : h.label,
          sublabel: `${h.orderCount} order${h.orderCount === 1 ? '' : 's'}`,
          emails: h.emails,
          settingsHint: h.settingsHint,
          note: fallbackNote(h),
          canAddEmail: h.canAddEmail,
          include: h.include,
          detailed: !!h.detailed,
          name: h.name
        }))}
        layoutChoice={haulierSelectionModal.layoutChoice || null}
        onDetailedChange={toggleDetailed}
        onToggle={toggleHaulier}
        onToggleAll={(on) => setHaulierSelectionModal(prev => ({ ...prev, hauliers: prev.hauliers.map(h => ({ ...h, include: on })) }))}
        sendToOffice={!!haulierSelectionModal.sendToOffice}
        onSendToOfficeChange={(on) => setHaulierSelectionModal(prev => ({ ...prev, sendToOffice: on }))}
        copyToOffice={!!haulierSelectionModal.copyToOffice}
        onCopyToOfficeChange={(on) => setHaulierSelectionModal(prev => ({ ...prev, copyToOffice: on }))}
        onSaveEmail={saveRecipientEmail}
        officeEmail={officeEmail}
        generating={!!haulierSelectionModal.generating}
        generatingText="Sending paperwork to driver(s)/ haulier(s)..."
        onSend={generatePaperwork}
      />

      {/* Office Summary Modal — shared with the other board */}
      {officeSummaryModal.open && (
        <OfficeSummaryModal
          warning={unplannedSelfBilledWarning(boardOrders, 'office')}
          generating={officeSummaryModal.generating}
          onClose={() => setOfficeSummaryModal({ open: false, generating: false })}
          onSend={sendOfficeSummary}
          user={user}
        />
      )}

      {/* Supplier Selection Modal */}
      {/* Mill order summaries — shared with the Bag board (PaperworkSendModal). */}
      <PaperworkSendModal
        user={user}
        isOpen={supplierSelectionModal.open}
        onClose={() => setSupplierSelectionModal({ open: false, suppliers: [] })}
        title="Send to Mills / Suppliers"
        intro="Each mill gets one email with its own orders for the day, with the spreadsheet attached."
        sendNoun="mill"
        settingsHint="Transport Settings → Mill Emails"
        recipients={supplierSelectionModal.suppliers.map(x => ({
          key: x.key,
          label: x.name,
          sublabel: `${x.orderCount} order${x.orderCount === 1 ? '' : 's'}`,
          emails: x.emails,
          emailNote: x.emailNote,
          include: x.include
        }))}
        onToggle={toggleSupplier}
        onToggleAll={(on) => setSupplierSelectionModal(prev => ({ ...prev, suppliers: prev.suppliers.map(x => ({ ...x, include: on })) }))}
        sendToOffice={!!supplierSelectionModal.sendToOffice}
        onSendToOfficeChange={(on) => setSupplierSelectionModal(prev => ({ ...prev, sendToOffice: on }))}
        officeEmail={officeEmail}
        generating={!!supplierSelectionModal.generating}
        generatingText="Sending order summaries to mills..."
        onSend={sendSupplierEmails}
      />

      <PaperworkHistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, kind: null })}
        date={selectedDate}
        kind={historyModal.kind}
        title={historyModal.kind === 'paperwork' ? 'Driver Paperwork History' : 'Mill Email History'}
      />
      <PlanHistoryModal isOpen={planHistoryOpen} onClose={() => setPlanHistoryOpen(false)} date={selectedDate} initialTab={planHistoryTab} />

      {/* Coming Soon Modal */}
      {comingSoonModal && createPortal(
        <div className="modal-overlay" onClick={() => setComingSoonModal(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send to Mills</h3>
              <button className="modal-close" onClick={() => setComingSoonModal(false)}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '2rem' }}>
              <span className="material-symbols-rounded" style={{ fontSize: '48px', color: 'var(--color-primary)' }}>construction</span>
              <p style={{ marginTop: '1rem' }}>This feature is coming soon!</p>
              <p className="progress-note">Automated mill orders will be available in a future update.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={() => setComingSoonModal(false)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
                <span className="btn-label">OK</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Revised POs after a date move — see offerResend. */}
      <ChangeQuantityModal row={changeQtyRow} onClose={() => setChangeQtyRow(null)} onDone={onQuantityChanged} />

      {resendOffer && !resendFlow && (
        <ConfirmDialog
          isOpen
          type="info"
          title="Send the revised purchase order?"
          message={resendOffer.message}
          confirmText="Send now"
          cancelText="Not now"
          onConfirm={() => setResendFlow(resendOffer.pos)}
          onCancel={() => setResendOffer(null)}
        />
      )}
      {resendFlow && (
        <SendToSupplierFlow
          linkedPos={resendFlow}
          orderNumber={resendFlow.map(p => p.purchase_order_number).join(', ')}
          user={user}
          onSent={() => setLastAutoSaved(new Date())}
          onClose={() => { setResendFlow(null); setResendOffer(null); fetchOrders(); }}
        />
      )}

      {/* Archive order… / Archive PO… (hooks/useBoardArchive) */}
      {rowArchive.element}
      {/* Refused — redeliver… (hooks/useBoardRedeliver) */}
      {rowRedeliver.element}
      {/* Outside haulier, no PO-H: raise and send it? (hooks/useOutsideHaulierPoh) */}
      {outsidePoh.element}

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.open}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ open: false, title: '', message: '', type: 'info' })}
      />

      {lorryDayOn && lorryWindow && (() => {
        const rows = orders.filter(o => inLorryDay(o) && lorryKeyOf(o) === lorryWindow);
        if (!rows.length) return null;
        const reg = String(rows[0].vehicle || '').trim();
        const driver = String(rows[0].driver || '').trim();
        const isUnknownLorry = reg === UNKNOWN_VEHICLE;
        // The day and lorry this window was opened on: a save is always that day's.
        const windowDate = selectedDate;
        const windowKey = lorryWindow;
        return (
          <LorryDayWindow
            key={`${selectedDate}|${lorryWindow}`}
            lorryKey={lorryWindow}
            rows={rows}
            vehicle={vehicleSpecFor(reg, driver)}
            lorryDay={(lorryDayState.lorryDays && lorryDayState.lorryDays[lorryWindow]) || null}
            canEdit={!lorryDayReadOnly}
            readOnlyReason={lorryDayReadOnly}
            unknownPayload={isUnknownLorry ? unknownPayloads[driver] : undefined}
            onSetUnknownPayload={isUnknownLorry && canEdit && !planFixed ? (payload) => setUnknownPayload(driver, payload) : null}
            onSave={(payload, opts) => saveLorryDayDraft(windowDate, windowKey, payload, opts)}
            onClose={() => {
              setLorryWindow(null);
              if (lorryDaySavedRef.current) { lorryDaySavedRef.current = false; fetchOrders(); }
            }}
          />
        );
      })()}

      {lorryDayOn && noteFor && (
        <PlannerNoteModal
          order={noteFor}
          note={normaliseLinePlan((orders.find(o => o.order_line_id === noteFor.order_line_id) || noteFor).line_plan).note}
          canEdit={!lorryDayReadOnly && !isCandidateRow(noteFor)}
          readOnlyReason={isCandidateRow(noteFor) ? 'Planned on another of its option dates' : lorryDayReadOnly}
          onSave={(note) => saveLinePlan(noteFor, { note })}
          onClose={() => setNoteFor(null)}
        />
      )}

      <VehicleLoadModal
        isOpen={loadModal.open && !!activeLoad}
        load={activeLoad}
        vehicle={activeLoad?.vehicle}
        // A locked or past day keeps its plan: the window's layouts are shown
        // but greyed with the reason (the server refuses them too).
        onApply={canEdit && !planFixed ? applyLoadConfig : null}
        applyBlocked={!canEdit ? noEditReason
          : planFixed ? (isPastDate ? 'This day has passed — its layout is kept as planned' : 'This day is locked — unlock it to change the layout') : null}
        onSetUnknownPayload={(canEdit && activeLoad?.isUnknown)
          ? (payload) => setUnknownPayload(activeLoad.unknownDriver, payload)
          : null}
        onClose={() => setLoadModal({ open: false, key: null })}
      />

      {/* "Go to order" from a row's context menu. Passing onEdit is what puts the
          edit button in the modal header (it's hidden once the order is invoiced). */}
      {orderModalId && (
        <CustomerOrderModal
          orderId={orderModalId}
          onClose={() => setOrderModalId(null)}
          onEdit={onNavigate ? handleEditOrder : undefined}
        />
      )}
    </div>
  );
}

export default TransportPlanning;
